import {
  Box,
  Grid,
  TextField,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import Header from "../../common/Header";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import Card from "../../common/Card";
import QuantityStepper from "../../common/QuantityStepper";
import {
  Add,
  AttributionOutlined,
  Close,
  DeleteOutline,
  InfoOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  PersonOutlined,
  Remove,
  StraightenOutlined,
  ViewInArOutlined,
  WidgetsOutlined,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { tokens } from "../../../theme";
import { useModalStyles } from "../../common/useModalStyles";
import { SectionHeader } from "../../ui/ContentSection";
import CustomDatePicker from "../../common/CustomDatePicker";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PracticeInfo, PracticeLocationInfo, fetchPracticeById, getAgreementByProductIdAndPracticeId, getPracticesByUserRole, resetPracticeByUserRole, resetPracticeInfoObject, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { UserRole, fetchAllUserRoles } from "../../../redux/slice/userSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { fullName, sortNames } from "../../../utils/Utility";
import { ORDERTYPE, USER_ROLE_ENTITY_NAMES, USER_ROLES } from "../../../constants/applicationConstants";
import { ProductItemInfo, fetchProductItemsByProductIdForOrder, fetchProductsByUserRoleData, fetchProductShippingConfigForOrder, useProductListReducer } from "../../../redux/slice/product/productListSlice";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { addOrder, AddOrderInfo, fetchOrderById, getOrderItemsByOrderId, GraftQty, OrderItemDTO } from "../../../redux/slice/order/OrderSlice";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { SingleAlertInfo, autoAddress, fetchPatientLookupByUserRole, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import StripePaymentTypePopup from "../payment/stripe/StripePaymentTypePopup";
import { CodeTable, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { fetchPracticeProductsData, fetchPracticeProductsDataUsingDateOfServiceAndProvider, fetchPracticeProductsDataWithModule, updatePracticeProductsInfoObject, usePracticeProductsSlice } from "../../../redux/slice/practice/practiceProductSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { fetchActiveLocationsForGivenDate, resetActivePracticeLocationsInfo, usePracticeLocationsSlice } from "../../../redux/slice/practice/practiceLocationSlice";
import OrderCalculationCard from "./OrderCalculationCard";
import { fetchManufacturerDataById } from "../../../redux/slice/manufacturer/ManufacturerSlice"; // CS-1845
import { getProductConfigByProduct } from "../../../service/ProductService";
import { stripeUpfrontOrderCheckout } from "../../../service/InvoiceService"; // CS-1845
import { usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import CustomTextField from "../../common/CustomTextField";
import { createOutsideOrder, updateOutsideOrder } from "../../../service/OrderService";
import InfoPopup from "../../common/InfoPopup";

interface Row {
  graftsNeeded: string;
  qty?: string;
  id: number;
};

type Option = {
  value: string | number,
  label: string
};

type productOption = {
  value: string | number,
  label: string,
  practiceProductId: string | undefined
};

type OutsideProductItem = {
  id: number;
  label: string;
  qty: number;
  price: string;
};

const sanitizeCurrencyInput = (value: string) => {
  const numericValue = value.replace(/[^0-9.]/g, "");
  const [whole = "", ...decimalParts] = numericValue.split(".");
  return decimalParts.length ? `${whole}.${decimalParts.join("")}` : whole;
};

const parseCurrencyInput = (value: string) => Number.parseFloat(value) || 0;

type OrderFormState = {
  practiceDropdown?: string,
  patientDropdown?: string,
  product?: number | string,
  provider: number | string,
  providerDropdown?: number | string,
  providerManual?: string,
  practiceManual?: string,
  patientManual?: string,
  productManual?: string,
  shippingMethodManual?: string,
  shippingAddressManual?: string,
  outsideShipPrice?: string,
  outsideNotes?: string,
  facilityAddress: number | string,
  graftQty: string[],
  requestedDeliveryDate: string,
  requestedShippingMethod: string,
  dateOfService: string,
  po: string,
  manufactureEmailNotifications: string,
  patientId?: number,
  patientInfo?: {
    patientFirstName: string,
    patientMiddleName: string,
    patientLastName: string,
    birthDate: string,
    gender: string
  }
  outsideOrderPrice?: string;
  orderDate: string,
}

// CS-1845 START - Upfront pricing config for create order
interface OrderProductConfigRule {
  productConfigId?: number;
  productId?: number;
  configType: string;
  configCode: string;
  value: string;
  metadata?: Record<string, unknown>;
}

type UpfrontPricingConfig = {
  enabled: boolean;
  upfrontDiscount: number;
  purchaseAndBill: boolean; // CS-2070 - Pay Now/Later toggle is gated on Purchase-and-Bill, independent of upfront pricing.
};

const PRODUCT_CONFIG_TYPE_UPFRONT_PRICING = "UPFRONT_PRICING";
const PRODUCT_CONFIG_TYPE_PURCHASE_AND_BILL = "PURCHASE_AND_BILL";
const PRODUCT_CONFIG_CODE_ENABLE = "ENABLE";
const PRODUCT_CONFIG_VALUE_YES = "Y";
const UPFRONT_DISCOUNT_METADATA_KEY = "upfrontDiscount";
// CS-1845 END

const parseJsonSafely = <T,>(value: unknown): T | null => {
  if (typeof value !== "string" || value.trim() === "") return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

type OutsideOrderEntryType = "PLATFORM" | "MANUAL" | string | null | undefined;

type SavedOutsideOrderItem = {
  label?: string;
  quantity?: number;
  unitPrice?: number;
  productItemId?: number | null;
  orderItemPrice?: number;
  orderItemId?: number;
  inactiveDate?: string | null;
  placeHolderOrderItem?: boolean | string | null;
};

const isPlaceholderOutsideSavedOrderItem = (item: SavedOutsideOrderItem) =>
  item.placeHolderOrderItem === true || item.placeHolderOrderItem === "true";

const isActiveSavedOutsideOrderItem = (inactiveDate?: string | null): boolean => {
  if (inactiveDate == null || String(inactiveDate).trim() === "") return true;
  const parsed = dayjs(inactiveDate);
  if (!parsed.isValid()) return true;
  return !parsed.isBefore(dayjs(), "day");
};

const parseActiveSavedOutsideOrderItems = (orderItemsJson: unknown): SavedOutsideOrderItem[] => {
  const parsed = parseJsonSafely<SavedOutsideOrderItem[]>(orderItemsJson) ?? [];
  return parsed.filter((item) => isActiveSavedOutsideOrderItem(item.inactiveDate));
};

type OutsideOrderEditBaselineItem = {
  orderItemId: number;
  productItemId: number | null;
  quantity: number;
  label: string;
  unitPrice: number;
  invoiceId?: number | null;
  placeHolderOrderItem?: boolean;
};

type OutsideOrderEditBaseline = {
  isManualProduct: boolean;
  practiceProductId: number | null;
  productId: number | null;
  manualProductName: string;
  items: OutsideOrderEditBaselineItem[];
  hasPlaceholderItemsOnly: boolean;
};

type OutsideOrderPayloadItem = {
  label: string;
  quantity: number;
  unitPrice: number;
  productItemId: number | null;
  isChanged?: boolean;
  quantityOnlyChange?: boolean;
  addedQuantity?: number;
  keepOrderItemIds?: number[];
};

type OutsideOrderEditChangeMeta = {
  productChanged: boolean;
  itemsChanged: boolean;
  inactivateOrderItemIds: number[];
  productItems: OutsideOrderPayloadItem[];
};

const normalizeOutsideText = (value: unknown) => String(value ?? "").trim().toLowerCase();

const toNullableOutsideNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isInvoicedOutsideOrderItem = (item: OutsideOrderEditBaselineItem) =>
  item.invoiceId != null && Number(item.invoiceId) > 0;

const pickOutsideOrderItemsToInactivate = (
  items: OutsideOrderEditBaselineItem[],
  count: number
): number[] => {
  if (count <= 0) return [];
  const uninvoiced = items.filter((item) => !isInvoicedOutsideOrderItem(item));
  const invoiced = items.filter((item) => isInvoicedOutsideOrderItem(item));
  return [...uninvoiced, ...invoiced].slice(0, count).map((item) => item.orderItemId);
};

const buildOutsideOrderInvoiceLookup = (savedOrder: any): Map<number, number | null> => {
  const map = new Map<number, number | null>();
  (savedOrder?.orderItems ?? []).forEach((item: OrderItemDTO) => {
    if (item?.orderItemId == null) return;
    const invoiceId = item.invoiceId ?? item.invoice?.invoiceId ?? null;
    map.set(Number(item.orderItemId), invoiceId != null && Number(invoiceId) > 0 ? Number(invoiceId) : null);
  });
  return map;
};

const enrichOutsideBaselineItem = (
  item: Omit<OutsideOrderEditBaselineItem, "invoiceId">,
  invoiceLookup: Map<number, number | null>
): OutsideOrderEditBaselineItem => ({
  ...item,
  invoiceId: invoiceLookup.get(item.orderItemId) ?? null,
});

const buildOutsideOrderEditBaselineItems = (
  savedOrder: any,
  isManualProduct = false
): OutsideOrderEditBaselineItem[] => {
  const info = savedOrder?.orderInfo ?? {};
  const invoiceLookup = buildOutsideOrderInvoiceLookup(savedOrder);
  const parsedFromInfo = parseActiveSavedOutsideOrderItems(info.orderItems);

  if (isManualProduct && parsedFromInfo.length) {
    return parsedFromInfo
      .filter((item) => item.orderItemId != null)
      .map((item) =>
        enrichOutsideBaselineItem(
          {
            orderItemId: Number(item.orderItemId),
            productItemId: item.productItemId ?? null,
            quantity: Number(item.quantity) || 1,
            label: String(item.label ?? "").trim(),
            unitPrice: Number(item.unitPrice ?? item.orderItemPrice ?? 0),
            placeHolderOrderItem: isPlaceholderOutsideSavedOrderItem(item),
          },
          invoiceLookup
        )
      );
  }

  const fromOrderItems = (savedOrder?.orderItems ?? [])
    .filter((item: OrderItemDTO & { inactiveDate?: string | null }) =>
      item?.orderItemId != null && isActiveSavedOutsideOrderItem(item.inactiveDate))
    .map((item: OrderItemDTO) =>
      enrichOutsideBaselineItem(
        {
          orderItemId: Number(item.orderItemId),
          productItemId: item.productItemId ?? null,
          quantity: Number(item.quantity) || 1,
          label: String(item.size ?? "").trim(),
          unitPrice: Number(item.orderItemPrice ?? 0),
        },
        invoiceLookup
      )
    );

  if (fromOrderItems.length) return fromOrderItems;

  return parsedFromInfo
    .filter((item) => item.orderItemId != null)
    .map((item) =>
      enrichOutsideBaselineItem(
        {
          orderItemId: Number(item.orderItemId),
          productItemId: item.productItemId ?? null,
          quantity: Number(item.quantity) || 1,
          label: String(item.label ?? "").trim(),
          unitPrice: Number(item.unitPrice ?? item.orderItemPrice ?? 0),
          placeHolderOrderItem: isPlaceholderOutsideSavedOrderItem(item),
        },
        invoiceLookup
      )
    );
};

const buildOutsideOrderEditBaseline = (
  savedOrder: any,
  isManualProduct: boolean
): OutsideOrderEditBaseline => {
  const info = savedOrder?.orderInfo ?? {};
  const items = buildOutsideOrderEditBaselineItems(savedOrder, isManualProduct);
  return {
    isManualProduct,
    practiceProductId: toNullableOutsideNumber(
      info.practiceProductId ?? savedOrder?.practiceProductId ?? savedOrder?.orderItems?.[0]?.practiceProductId
    ),
    productId: toNullableOutsideNumber(savedOrder?.productId),
    manualProductName: String(info.productName ?? savedOrder?.productName ?? "").trim(),
    items,
    hasPlaceholderItemsOnly:
      isManualProduct &&
      items.length > 0 &&
      items.every((item) => item.placeHolderOrderItem),
  };
};

const normalizeOutsideUnitPrice = (value: unknown) => Number(Number(value ?? 0).toFixed(2));

const manualOutsideLineIdentityMatches = (
  baseline: { label: string; unitPrice: number },
  current: { label: string; unitPrice: number }
) =>
  normalizeOutsideText(baseline.label) === normalizeOutsideText(current.label) &&
  normalizeOutsideUnitPrice(baseline.unitPrice) === normalizeOutsideUnitPrice(current.unitPrice);

const manualOutsideLineMatches = (
  baseline: { label: string; quantity: number; unitPrice: number },
  current: { label: string; quantity: number; unitPrice: number }
) =>
  manualOutsideLineIdentityMatches(baseline, current) && baseline.quantity === current.quantity;

const platformOutsideLineIdentityMatches = (
  baseline: { unitPrice: number },
  current: { unitPrice: number }
) => normalizeOutsideUnitPrice(baseline.unitPrice) === normalizeOutsideUnitPrice(current.unitPrice);

const platformOutsideLineMatches = (
  baseline: { quantity: number; unitPrice: number },
  current: { quantity: number; unitPrice: number }
) =>
  platformOutsideLineIdentityMatches(baseline, current) && baseline.quantity === current.quantity;

const isSameOutsidePlatformProduct = (
  baseline: OutsideOrderEditBaseline,
  current: { practiceProductId: number | null; productId: number | null }
) => {
  if (baseline.practiceProductId != null && current.practiceProductId != null) {
    return baseline.practiceProductId === current.practiceProductId;
  }
  if (baseline.productId != null && current.productId != null) {
    return baseline.productId === current.productId;
  }
  return false;
};

const withOutsideItemChangeFlag = (
  item: OutsideOrderPayloadItem,
  isChanged: boolean,
  extras?: Pick<OutsideOrderPayloadItem, "quantityOnlyChange" | "addedQuantity" | "keepOrderItemIds">
): OutsideOrderPayloadItem => {
  const payload: OutsideOrderPayloadItem = {
    label: item.label,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    productItemId: item.productItemId,
    isChanged,
  };

  if (extras?.quantityOnlyChange) {
    payload.quantityOnlyChange = true;
    if (extras.addedQuantity != null && extras.addedQuantity > 0) {
      payload.addedQuantity = extras.addedQuantity;
    }
    if (extras.keepOrderItemIds?.length) {
      payload.keepOrderItemIds = extras.keepOrderItemIds;
    }
  }

  return payload;
};

const resolveOutsideQuantityOnlyChange = (
  baseOrderItems: OutsideOrderEditBaselineItem[],
  currentQuantity: number,
  inactivateOrderItemIds: number[]
): Pick<OutsideOrderPayloadItem, "quantityOnlyChange" | "addedQuantity" | "keepOrderItemIds"> | null => {
  const baselineQuantity = baseOrderItems.reduce((sum, item) => sum + item.quantity, 0);
  if (currentQuantity === baselineQuantity) return null;

  const keepOrderItemIds = baseOrderItems.map((item) => item.orderItemId);

  if (currentQuantity > baselineQuantity) {
    return {
      quantityOnlyChange: true,
      addedQuantity: currentQuantity - baselineQuantity,
      keepOrderItemIds,
    };
  }

  const removeCount = baselineQuantity - currentQuantity;
  const idsToInactivate = pickOutsideOrderItemsToInactivate(baseOrderItems, removeCount);
  inactivateOrderItemIds.push(...idsToInactivate);

  return {
    quantityOnlyChange: true,
    keepOrderItemIds: keepOrderItemIds.filter((id) => !idsToInactivate.includes(id)),
  };
};

const computeOutsideOrderEditChangeMeta = (
  baseline: OutsideOrderEditBaseline | null,
  current: {
    isManualProduct: boolean;
    practiceProductId: number | null;
    productId: number | null;
    manualProductName: string;
    platformItems: OutsideOrderPayloadItem[];
    manualItems: OutsideOrderPayloadItem[];
  }
): OutsideOrderEditChangeMeta => {
  const currentItems = current.isManualProduct ? current.manualItems : current.platformItems;

  if (!baseline) {
    return {
      productChanged: false,
      itemsChanged: false,
      inactivateOrderItemIds: [],
      productItems: currentItems.map((item) => withOutsideItemChangeFlag(item, true)),
    };
  }

  const productChanged =
    baseline.isManualProduct !== current.isManualProduct ||
    (current.isManualProduct
      ? normalizeOutsideText(baseline.manualProductName) !== normalizeOutsideText(current.manualProductName)
      : !isSameOutsidePlatformProduct(baseline, current));

  if (productChanged) {
    return {
      productChanged: true,
      itemsChanged: currentItems.length > 0,
      inactivateOrderItemIds: baseline.items.map((item) => item.orderItemId),
      productItems: currentItems.map((item) => withOutsideItemChangeFlag(item, true)),
    };
  }

  if (
    current.isManualProduct &&
    baseline.isManualProduct &&
    currentItems.length === 0 &&
    baseline.hasPlaceholderItemsOnly
  ) {
    return {
      productChanged: false,
      itemsChanged: false,
      inactivateOrderItemIds: [],
      productItems: [],
    };
  }

  const inactivateOrderItemIds: number[] = [];
  let itemsChanged = false;

  if (current.isManualProduct) {
    type ManualAggregate = {
      label: string;
      quantity: number;
      unitPrice: number;
      orderItems: OutsideOrderEditBaselineItem[];
    };
    const baselineMap = new Map<string, ManualAggregate>();

    baseline.items.forEach((item) => {
      const key = `${normalizeOutsideText(item.label)}|${normalizeOutsideUnitPrice(item.unitPrice)}`;
      const existing = baselineMap.get(key);
      if (existing) {
        existing.quantity += item.quantity;
        existing.orderItems.push(item);
        return;
      }
      baselineMap.set(key, {
        label: item.label,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        orderItems: [item],
      });
    });

    const matchedBaselineKeys = new Set<string>();
    const flaggedItems = currentItems.map((item) => {
      const key = `${normalizeOutsideText(item.label)}|${normalizeOutsideUnitPrice(item.unitPrice)}`;
      const base = baselineMap.get(key);

      if (!base) {
        itemsChanged = true;
        return withOutsideItemChangeFlag(item, true);
      }

      if (manualOutsideLineMatches(base, item)) {
        matchedBaselineKeys.add(key);
        return withOutsideItemChangeFlag(item, false);
      }

      itemsChanged = true;

      if (manualOutsideLineIdentityMatches(base, item)) {
        const quantityExtras = resolveOutsideQuantityOnlyChange(base.orderItems, item.quantity, inactivateOrderItemIds);
        matchedBaselineKeys.add(key);
        return withOutsideItemChangeFlag(item, true, quantityExtras ?? undefined);
      }

      inactivateOrderItemIds.push(...base.orderItems.map((orderItem) => orderItem.orderItemId));
      matchedBaselineKeys.add(key);
      return withOutsideItemChangeFlag(item, true);
    });

    baselineMap.forEach((base, key) => {
      if (!matchedBaselineKeys.has(key)) {
        itemsChanged = true;
        inactivateOrderItemIds.push(...base.orderItems.map((orderItem) => orderItem.orderItemId));
      }
    });

    return {
      productChanged: false,
      itemsChanged,
      inactivateOrderItemIds: Array.from(new Set(inactivateOrderItemIds)),
      productItems: flaggedItems,
    };
  }

  type PlatformAggregate = {
    quantity: number;
    unitPrice: number;
    orderItems: OutsideOrderEditBaselineItem[];
  };
  const baselineMap = new Map<number, PlatformAggregate>();

  baseline.items.forEach((item) => {
    if (item.productItemId == null) return;
    const productItemId = Number(item.productItemId);
    const existing = baselineMap.get(productItemId);
    if (existing) {
      existing.quantity += item.quantity;
      existing.orderItems.push(item);
      return;
    }
    baselineMap.set(productItemId, {
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      orderItems: [item],
    });
  });

  const matchedProductItemIds = new Set<number>();
  const flaggedItems = currentItems.map((item) => {
    const productItemId = item.productItemId == null ? null : Number(item.productItemId);
    if (productItemId == null) {
      itemsChanged = true;
      return withOutsideItemChangeFlag(item, true);
    }

    const base = baselineMap.get(productItemId);
    if (!base) {
      itemsChanged = true;
      return withOutsideItemChangeFlag(item, true);
    }

    if (platformOutsideLineMatches(base, item)) {
      matchedProductItemIds.add(productItemId);
      return withOutsideItemChangeFlag(item, false);
    }

    itemsChanged = true;

    if (platformOutsideLineIdentityMatches(base, item)) {
      const quantityExtras = resolveOutsideQuantityOnlyChange(base.orderItems, item.quantity, inactivateOrderItemIds);
      matchedProductItemIds.add(productItemId);
      return withOutsideItemChangeFlag(item, true, quantityExtras ?? undefined);
    }

    inactivateOrderItemIds.push(...base.orderItems.map((orderItem) => orderItem.orderItemId));
    matchedProductItemIds.add(productItemId);
    return withOutsideItemChangeFlag(item, true);
  });

  baselineMap.forEach((base, productItemId) => {
    if (!matchedProductItemIds.has(productItemId)) {
      itemsChanged = true;
      inactivateOrderItemIds.push(...base.orderItems.map((orderItem) => orderItem.orderItemId));
    }
  });

  return {
    productChanged: false,
    itemsChanged,
    inactivateOrderItemIds: Array.from(new Set(inactivateOrderItemIds)),
    productItems: flaggedItems,
  };
};

const isManualEntryType = (entryType: OutsideOrderEntryType) => Boolean(entryType) && entryType !== "PLATFORM";

const isOutsideOrderDetails = (details?: {
  isOutsideOrder?: boolean | string;
  orderInfo?: { isOutsideOrder?: boolean | string };
}) =>
  details?.isOutsideOrder === true ||
  details?.isOutsideOrder === "true" ||
  details?.orderInfo?.isOutsideOrder === true ||
  details?.orderInfo?.isOutsideOrder === "true";

const getReorderProviderId = (details: any) => {
  if (!details) return null;
  const info = details.orderInfo ?? {};
  if (info.providerEntryType === "MANUAL") return details.providerId ?? null;
  const id = info.providerId ?? details.providerId ?? details.practiceProviderId;
  if (id == null || id === "") return null;
  const parsed = Number(id);
  return Number.isNaN(parsed) ? id : parsed;
};

const getReorderLocationId = (details: any) => {
  if (!details) return null;
  const info = details.orderInfo ?? {};
  if (info.shippingAddressEntryType === "MANUAL") return details.locationId ?? null;
  const id = info.shippingAddressId ?? info.facilityAddress ?? details.locationId;
  if (id == null || id === "") return null;
  const parsed = Number(id);
  return Number.isNaN(parsed) ? id : parsed;
};

const normalizeReorderOrderDetails = (details: any) => {
  if (!details) return details;
  const info = details.orderInfo ?? {};
  const toNumericId = (value: any) => {
    if (value == null || value === "") return value;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  };
  return {
    ...details,
    providerName: details.providerName ?? info.provider ?? details.providerName,
    practiceProductId: toNumericId(
      info.practiceProductId ??
      details.practiceProductId ??
      details.orderItems?.[0]?.practiceProductId
    ),
    providerId: getReorderProviderId(details),
    locationId: getReorderLocationId(details),
  };
};

const aggregateSavedOutsideOrderItems = (items: SavedOutsideOrderItem[]): OutsideProductItem[] => {
  const groupedItems = new Map<string, OutsideProductItem>();

  items
    .filter((item) => item.label)
    .filter((item) => isActiveSavedOutsideOrderItem(item.inactiveDate))
    .forEach((item, index) => {
      const unitPrice = Number(item.unitPrice ?? item.orderItemPrice ?? 0);
      const productItemId = item.productItemId ?? null;
      const key = JSON.stringify({
        label: item.label,
        unitPrice,
        productItemId,
      });
      const existingItem = groupedItems.get(key);

      if (existingItem) {
        groupedItems.set(key, {
          ...existingItem,
          qty: existingItem.qty + (Number(item.quantity) || 1),
        });
        return;
      }

      groupedItems.set(key, {
        id: item.orderItemId ?? productItemId ?? Date.now() + index,
        label: item.label ?? "",
        qty: Number(item.quantity) || 1,
        price: unitPrice ? String(unitPrice) : "",
      });
    });

  return Array.from(groupedItems.values());
};

type OrderItemPriceSource = {
  productItemId?: number | string | null;
  orderItemPrice?: number | string | null;
  unitPrice?: number | string | null;
};

const registerOutsideEditPlatformOrderItemPrices = (
  target: Record<number, number>,
  items?: OrderItemPriceSource[] | null
) => {
  items?.forEach((item) => {
    const productItemId = item.productItemId;
    if (productItemId == null || String(productItemId).trim() === "") return;

    const price = item.orderItemPrice ?? item.unitPrice;
    if (price == null || String(price).trim() === "") return;

    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice)) return;
    target[Number(productItemId)] = numericPrice;
  });
};

const formatOrderDateValue = (value?: string | null) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
  }
  return value;
};

const isValidOrderDateValue = (value?: unknown) => {
  if (value == null) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "Invalid Date") return false;
    return dayjs(trimmed, "MM/DD/YYYY", true).isValid();
  }
  const parsed = dayjs(value as dayjs.ConfigType);
  return parsed.isValid();
};

export type PracticeProductAgreementDTO = {
  practiceProductId: number;
  practiceName: string;
  productName: string;
  extendedPrice: number;
  discountType: string;
  effectiveDate: string;
  inactiveDate: string | null;
  agreementStatus: string;
  practiceSignedDate: string | null;
  manufacturerSignedDate: string | null;
  productLogo: any;
  upfrontPrice?: number | null;
  manufacturerStripeConfig?: any; // CS-1845
};

/** Upfront create-order returns numeric orderId in API `data`; other flows may return an order DTO. */
const resolveCreatedOrderId = (payload: unknown): number | null => {
  if (payload == null) return null;
  if (typeof payload === "number" && Number.isFinite(payload)) return payload;
  if (typeof payload === "string" && payload.trim() !== "" && Number.isFinite(Number(payload))) {
    return Number(payload);
  }
  if (typeof payload === "object") {
    const orderId = (payload as { orderId?: unknown }).orderId;
    if (orderId != null && Number.isFinite(Number(orderId))) return Number(orderId);
  }
  return null;
};

const Order = () => {
  const { control, handleSubmit, reset, watch, getValues, setValue, unregister, clearErrors, formState: { errors } } = useForm<OrderFormState>();
  const { navigate } = useNavigation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const colors = tokens(theme.palette.mode);
  const { tokens: mTokens, dropdownWrapSx, datepickerWrapSx, cancelBtnSx, primaryBtnSx, inputFieldSx } = useModalStyles();
  const cardSx = {
    borderRadius: "16px", border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg, backgroundImage: "none", overflow: "visible",
  };
  const { practiceByUserRole } = usePracticeSlice();
  const { productsByUserRole, productShippingConfigForOrder } = useProductListReducer();
  const { PracticeSaveInfo } = usePracticeSlice();
  const { userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const { practiceProductsInfo } = usePracticeProductsSlice();
  const [order, setOrder] = useState<{ orderType: string, orderDetails: any, orderDate: string, isReorder?: boolean }>({
    orderType: "",
    orderDetails: {},
    orderDate: "",
    isReorder: false
  });
  const [productIVR, setProductIVR] = useState<ProductItemInfo[]>();
  const [listData, setListData] = useState<Row[]>([]);
  const [locationDetails, setLocationDetails] = useState<Option>();
  const [selectedProduct, setSelectedProduct] = useState<productOption | null>();
  const [graftsQtyList, setGraftsQtyList] = useState<GraftQty[]>();
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });
  // const [shippingMethods, setShippingMethods] = useState<CodeTable[]>([]);
  const [isDisableProducts, setIsDisableProducts] = useState(true);
  const [isPracticeUser, setIsPracticeUser] = useState(false);
  const [isMultiPracticeUser, setIsMultiPracticeUser] = useState(false);
  const [showStripePaymentTypePopup, setShowStripePaymentTypePopup] = useState(false);
  const [upfrontOrderResponse, setUpfrontOrderResponse] = useState<any>(null);
  const [pendingUpfrontOrderPayload, setPendingUpfrontOrderPayload] = useState<any>(null); // CS-1845
  const [mfrStripeConfig, setMfrStripeConfig] = useState<any>(null); // CS-1845
  const [isProcessingStripePayment, setIsProcessingStripePayment] = useState(false); // CS-1845 - Loading state for Stripe checkout
  const [checkoutError, setCheckoutError] = useState<string | null>(null); // CS-1845 - Error message to show inside the payment popup
  const [validPP, setValidPP] = useState<PracticeProductAgreementDTO | null>(null);
  const [manufacturerEmailNotifications, setManufacturerEmailNotifications] = useState<boolean>(true);
  const [showFutureOrderDateWarning, setShowFutureOrderDateWarning] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const { activePracticeLocations } = usePracticeLocationsSlice();
  const initialProviderScopedProductsLoaded = useRef(false);
  const orderItemsLoaded = useRef(false);
  const [sizeQtyRows, setSizeQtyRows] = useState<Array<{ key: string; productItemId: number | null; quantity: number }>>([]);
  const sizeQtyPrevKeysRef = useRef<Set<string>>(new Set());
  const [upfrontPricingConfig, setUpfrontPricingConfig] = useState<UpfrontPricingConfig>({
    enabled: false,
    upfrontDiscount: 0,
    purchaseAndBill: false,
  }); // CS-1845
  // CS-1845 - Exact total reported by LiveOrderSummary via onTotalCalculated (upfront path).
  // This is the single source of truth — no recalculation in Order.tsx.
  const [upfrontCardTotal, setUpfrontCardTotal] = useState<number>(0);
  const [sidebarPaymentMode, setSidebarPaymentMode] = useState<"now" | "later" | null>(null);

  const isPastOrder = location?.state?.orderType === ORDERTYPE.PAST_ORDER;
  const isReorder = location?.state?.isReorder === true;
  const isOutsideRecordOrder = isPastOrder && !isReorder;
  const canEnterOutsideManual = isOutsideRecordOrder;
  const outsideOrderEditOrderId = location?.state?.outsideOrderEditOrderId;
  const isOutsideOrderEdit = Boolean(outsideOrderEditOrderId);
  const { patientsByUserRole } = usePatientReducer();
  const [outsideManual, setOutsideManual] = useState<{
    practice: boolean; provider: boolean; patient: boolean; product: boolean; shippingMethod: boolean; shippingAddress: boolean;
  }>({ practice: false, provider: false, patient: false, product: false, shippingMethod: false, shippingAddress: false });
  const hasOutsideManualField =
    outsideManual.practice ||
    outsideManual.provider ||
    outsideManual.patient ||
    outsideManual.product ||
    outsideManual.shippingMethod ||
    outsideManual.shippingAddress;
  const shouldSaveOutsideOrder = !isReorder && (isOutsideOrderEdit || hasOutsideManualField);
  const requireDateOfService = !shouldSaveOutsideOrder;
  const requireShippingAddress = isPastOrder && !shouldSaveOutsideOrder;
  const dateOfServiceRules = requireDateOfService
    ? {
        required: "Date of service is required.",
        validate: (value: unknown) =>
          isValidOrderDateValue(value) || "Date of service is required.",
      }
    : {
        validate: (value: unknown) =>
          !value || isValidOrderDateValue(value) || "Invalid date of service.",
      };
  const [outsidePatientList, setOutsidePatientList] = useState<any[]>([]);
  const [outsideProductItems, setOutsideProductItems] = useState<OutsideProductItem[]>([]);
  const addOutsideProductItem = () => setOutsideProductItems(prev => [...prev, { id: Date.now() + Math.random(), label: "", qty: 1, price: "" }]);
  const setOutsideProductItem = (id: number, patch: Partial<OutsideProductItem>) =>
    setOutsideProductItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  const delOutsideProductItem = (id: number) => setOutsideProductItems(prev => prev.filter(it => it.id !== id));
  const lastOutsideItemsOrderPriceRef = useRef<string | null>(null);
  const watchedOutsideShipPrice = watch("outsideShipPrice" as any);
  const pendingManualPrefill = useRef<Partial<Record<"practice" | "provider" | "patient" | "product" | "shippingMethod" | "shippingAddress", string>>>({});
  const [isSavingOutside, setIsSavingOutside] = useState(false);
  const outsideOrderEditHydrated = useRef<number | null>(null);
  const outsidePlatformReorderHydrated = useRef(false);
  const outsideOrderEditManualValues = useRef<Partial<Record<"practice" | "provider" | "patient" | "product" | "shippingMethod" | "shippingAddress", string>>>({});
  const outsideOrderEditUserModified = useRef(false);
  const outsideOrderEditBaseline = useRef<OutsideOrderEditBaseline | null>(null);
  const shippingMethodHydratedRef = useRef(false);
  const [outsideEditPlatformOrderItemPrices, setOutsideEditPlatformOrderItemPrices] = useState<Record<number, number>>({});
  const watchedProductField = watch("product");

  const mergeOutsideEditPlatformOrderItemPrices = useCallback(
    (items?: OrderItemPriceSource[] | null) => {
      if (!items?.length) return;
      setOutsideEditPlatformOrderItemPrices((prev) => {
        const next = { ...prev };
        registerOutsideEditPlatformOrderItemPrices(next, items);
        return next;
      });
    },
    []
  );

  const resetOutsideEditPlatformOrderItemPrices = useCallback(() => {
    setOutsideEditPlatformOrderItemPrices({});
  }, []);

  type OutsideManualFlags = typeof outsideManual;

  const clearPlatformShippingMethod = () => {
    setValue("requestedShippingMethod", "");
  };

  const clearPlatformProductAndSizes = (clearPlatformShipping = true) => {
    setSelectedProduct(null);
    setValue("product", "");
    setValue("graftQty", []);
    setGraftsQtyList([]);
    setProductIVR(undefined);
    setListData([]);
    setSizeQtyRows([]);
    sizeQtyPrevKeysRef.current = new Set();
    if (isOutsideOrderEdit) {
      resetOutsideEditPlatformOrderItemPrices();
    }
    if (!isOutsideOrderEdit) {
      orderItemsLoaded.current = false;
    }
    if (clearPlatformShipping) {
      clearPlatformShippingMethod();
    }
    setValidPP(null);
    setMfrStripeConfig(null);
    setUpfrontPricingConfig({ enabled: false, upfrontDiscount: 0, purchaseAndBill: false });
    setUpfrontOrderResponse(null);
    setPendingUpfrontOrderPayload(null);
    setCheckoutError(null);
    setSidebarPaymentMode(null);
  };

  const resetSelectedProductState = () => {
    clearPlatformProductAndSizes(true);
    setOutsideProductItems([]);
    setValue("outsideOrderPrice" as any, "");
  };

  const clearPracticeDependentFields = (manual: OutsideManualFlags = outsideManual) => {
    if (!manual.provider) {
      setValue("providerDropdown" as any, "");
    }
    if (!manual.patient) {
      setValue("patientDropdown" as any, "");
    }
    if (!manual.product) {
      clearPlatformProductAndSizes(!manual.shippingMethod);
    } else if (!manual.shippingMethod) {
      clearPlatformShippingMethod();
    }
    if (!manual.shippingAddress) {
      setValue("facilityAddress", "");
      setSelectedLocation("");
    }
    setOutsidePatientList([]);
    dispatch(updatePracticeProductsInfoObject([]));
    dispatch(resetPracticeInfoObject());
    if (isPastOrder) {
      dispatch(resetActivePracticeLocationsInfo());
    }
  };

  const clearProviderDependentFields = (manual: OutsideManualFlags = outsideManual) => {
    setValue("providerDropdown" as any, "");
    if (!manual.provider && !manual.product) {
      clearPlatformProductAndSizes(!manual.shippingMethod);
    }
  };

  const orderType = location?.state?.orderType;
  const orderDetails = location?.state?.orderDetails;
  const receivedOrderDate = location?.state?.orderDate;
  const showPatientFromLocation =
    location.state?.orderType === ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR ||
    order?.orderType === ORDERTYPE.PAST_ORDER;
  const showPatientFromOrder = order?.orderType === ORDERTYPE.DIRECT_ORDER;

  const allowedEntities = [USER_ROLE_ENTITY_NAMES.ADMIN, USER_ROLE_ENTITY_NAMES.PRACTICE, USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE];
  const canSeePracticeDropdown = allowedEntities.includes(userInfo?.entityName) && order?.orderType !== ORDERTYPE.DIRECT_ORDER;

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  const goOutsideManual = (
    field: "practice" | "provider" | "patient" | "product" | "shippingMethod" | "shippingAddress",
    typedValue?: string
  ) => {
    outsideOrderEditUserModified.current = true;
    if (typedValue) {
      pendingManualPrefill.current[field] = typedValue;
    } else {
      delete pendingManualPrefill.current[field];
    }
    setOutsideManual(p => {
      const next = { ...p, [field]: true };
      if (field === "practice") {
        outsideOrderEditManualValues.current.practice = typedValue ?? "";
        setValue("practiceDropdown", "");
        setValue("practiceManual" as any, typedValue ?? "");
        clearPracticeDependentFields(next);
      }
      if (field === "provider") {
        outsideOrderEditManualValues.current.provider = typedValue ?? "";
        setValue("providerDropdown" as any, "");
        if (!p.product) {
          clearPlatformProductAndSizes(!p.shippingMethod);
        }
      }
      if (field === "patient") {
        if (!p.patient) setValue("patientDropdown" as any, "");
      }
      if (field === "product") {
        setValue("product", "");
        setOutsideProductItems([]);
        setValue("outsideOrderPrice" as any, "");
        clearPlatformProductAndSizes(!next.shippingMethod);
      }
      if (field === "shippingMethod") {
        clearPlatformShippingMethod();
      }
      if (field === "shippingAddress") {
        setValue("facilityAddress", "");
      }
      return next;
    });
  };

  useEffect(() => {
    const fieldMap: Record<string, string> = {
      practice: "practiceManual",
      provider: "providerManual",
      patient: "patientManual",
      product: "productManual",
      shippingMethod: "shippingMethodManual",
      shippingAddress: "shippingAddressManual",
    };

    (Object.keys(outsideManual) as Array<keyof typeof outsideManual>).forEach((field) => {
      if (outsideManual[field] && pendingManualPrefill.current[field]) {
        setValue(fieldMap[field] as any, pendingManualPrefill.current[field]);
        delete pendingManualPrefill.current[field];
      }
    });
  }, [outsideManual]);

  useEffect(() => {
    if (requireDateOfService && requireShippingAddress) return;
    clearErrors("dateOfService");
    clearErrors("facilityAddress");
    clearErrors("shippingAddressManual");
  }, [requireDateOfService, requireShippingAddress, clearErrors]);

  const goOutsideSystem = (field: "practice" | "provider" | "patient" | "product" | "shippingMethod" | "shippingAddress") => {
    outsideOrderEditUserModified.current = true;
    const next = { ...outsideManual, [field]: false };
    setOutsideManual(next);
    if (field === "practice") {
      setValue("practiceManual" as any, "");
      setValue("practiceDropdown", "");
      clearPracticeDependentFields(next);
    }
    if (field === "provider") {
      setValue("providerManual", "");
      setValue("providerDropdown" as any, "");
    }
    if (field === "patient") setValue("patientManual" as any, "");
    if (field === "product") {
      setValue("productManual" as any, "");
      setOutsideProductItems([]);
      setValue("outsideOrderPrice" as any, "");
      clearPlatformProductAndSizes(!next.shippingMethod);
      orderItemsLoaded.current = true;
    }
    if (field === "shippingMethod") {
      setValue("shippingMethodManual" as any, "");
      setValue("outsideShipPrice" as any, "");
      clearPlatformShippingMethod();
    }
    if (field === "shippingAddress") setValue("shippingAddressManual" as any, "");
  };

  useEffect(() => {
    if (!location.state || isOutsideOrderEdit) return;
    setValue('practiceDropdown', location.state.practiceId)
    setValue('patientDropdown', location.state?.patientId)
    setValue('patientId', location.state?.patientId)
    setValue('patientInfo.patientFirstName', location.state?.patientData?.firstName)
    setValue('patientInfo.patientMiddleName', location.state?.patientData?.middleName)
    setValue('patientInfo.patientLastName', location.state?.patientData?.lastName)
    setValue('patientInfo.birthDate', location.state?.patientData?.dob)
    setValue('patientInfo.gender', location.state?.patientData?.gender)

  }, [location.state, isOutsideOrderEdit, setValue])

  useEffect(() => {
    if (!outsideOrderEditOrderId || outsideOrderEditHydrated.current === Number(outsideOrderEditOrderId)) return;

    const hydrateOutsideOrderEdit = async () => {
      const response = await dispatch(fetchOrderById({
        orderId: Number(outsideOrderEditOrderId),
        isSaveAuditLog: false,
      }));

      

      const savedOrder = response?.payload?.data;
      if (!savedOrder) return;
      console.log(savedOrder)
      outsideOrderEditHydrated.current = Number(outsideOrderEditOrderId);
      const info = savedOrder.orderInfo ?? {};
      const normalizedOrder = {
        ...savedOrder,
        practiceProductId: info.practiceProductId ?? savedOrder.practiceProductId ?? savedOrder.orderItems?.[0]?.practiceProductId,
        requestedShippingMethod: info.shippingMethod ?? savedOrder.requestedShippingMethod,
        locationId: info.shippingAddressId ?? savedOrder.locationId,
      };

      setOrder({
        orderType: ORDERTYPE.PAST_ORDER,
        orderDetails: normalizedOrder,
        orderDate: formatOrderDateValue(savedOrder.orderDate),
      });

      const nextOutsideManual = {
        practice: isManualEntryType(info.practiceEntryType),
        provider: isManualEntryType(info.providerEntryType),
        patient: isManualEntryType(info.patientEntryType),
        product: isManualEntryType(info.productEntryType),
        shippingMethod: isManualEntryType(info.shippingMethodEntryType),
        shippingAddress: isManualEntryType(info.shippingAddressEntryType),
      };

      outsideOrderEditManualValues.current = {
        practice: info.practiceName ?? savedOrder.practiceName ?? "",
        provider: info.providerName ?? savedOrder.providerName ?? "",
        patient: info.patientName ?? savedOrder.patientName ?? "",
        product: info.productName ?? savedOrder.productName ?? "",
        shippingMethod: info.shippingMethod ?? savedOrder.requestedShippingMethod ?? "",
        shippingAddress: info.shippingAddress ?? "",
      };
      (Object.keys(nextOutsideManual) as Array<keyof typeof nextOutsideManual>).forEach((field) => {
        if (nextOutsideManual[field] && outsideOrderEditManualValues.current[field]) {
          pendingManualPrefill.current[field] = outsideOrderEditManualValues.current[field];
        }
      });

      setOutsideManual(nextOutsideManual);
      outsideOrderEditBaseline.current = buildOutsideOrderEditBaseline(savedOrder, nextOutsideManual.product);
      setValue("orderDate", formatOrderDateValue(savedOrder.orderDate));
      setValue("dateOfService", formatOrderDateValue(savedOrder.dateOfService ?? info.dateOfService));
      setValue("outsideNotes" as any, info.notes ?? "");
      setValue("outsideOrderPrice" as any, String(info.orderPrice ?? savedOrder.orderTotalPrice ?? ""));
      setValue("outsideShipPrice" as any, String(info.shippingPrice ?? savedOrder.outsideOrderShippingPrice ?? ""));

      if (nextOutsideManual.practice) {
        setValue("practiceManual" as any, info.practiceName ?? savedOrder.practiceName ?? "");
      } else {
        setValue("practiceDropdown", String(info.practiceId ?? savedOrder.practiceId ?? ""));
      }

      if (nextOutsideManual.provider) {
        setValue("providerManual", info.providerName ?? savedOrder.providerName ?? "");
      } else {
        setValue("providerDropdown" as any, String(info.providerId ?? savedOrder.providerId ?? ""));
      }

      if (nextOutsideManual.patient) {
        setValue("patientManual" as any, info.patientName ?? savedOrder.patientName ?? "");
      } else {
        setValue("patientDropdown" as any, String(savedOrder.patientId ?? ""));
      }

      if (nextOutsideManual.product) {
        setValue("productManual" as any, info.productName ?? savedOrder.productName ?? "");
        const savedItems = parseActiveSavedOutsideOrderItems(info.orderItems);
        setOutsideProductItems(aggregateSavedOutsideOrderItems(savedItems));
        setSizeQtyRows([]);
        setGraftsQtyList([]);
        orderItemsLoaded.current = true;
      } else {
        setValue("product", String(info.practiceProductId ?? savedOrder.orderItems?.[0]?.practiceProductId ?? ""));
      }

      if (nextOutsideManual.shippingMethod) {
        setValue("shippingMethodManual" as any, info.shippingMethod ?? savedOrder.requestedShippingMethod ?? "");
      } else {
        setValue("requestedShippingMethod", String(info.shippingMethodId ?? ""));
      }

      if (nextOutsideManual.shippingAddress) {
        setValue("shippingAddressManual" as any, info.shippingAddress ?? "");
      } else {
        setValue("facilityAddress", String(info.shippingAddressId ?? ""));
        setSelectedLocation(String(info.shippingAddressId ?? ""));
      }

      if (!nextOutsideManual.product) {
        const prices: Record<number, number> = {};
        const activeOrderItems = (savedOrder.orderItems ?? []).filter(
          (item: OrderItemDTO & { inactiveDate?: string | null }) =>
            isActiveSavedOutsideOrderItem(item.inactiveDate)
        );
        registerOutsideEditPlatformOrderItemPrices(prices, activeOrderItems);
        registerOutsideEditPlatformOrderItemPrices(
          prices,
          parseActiveSavedOutsideOrderItems(info.orderItems)
        );
        setOutsideEditPlatformOrderItemPrices(prices);
      }

      if (!nextOutsideManual.product && savedOrder.orderItems?.length) {
        const quantityMap = new Map<number, number>();
        savedOrder.orderItems.forEach((item: OrderItemDTO & { inactiveDate?: string | null }) => {
          if (!item.productItemId) return;
          if (!isActiveSavedOutsideOrderItem(item.inactiveDate)) return;
          quantityMap.set(item.productItemId, (quantityMap.get(item.productItemId) || 0) + (Number(item.quantity) || 0));
        });
        const graftList = Array.from(quantityMap.entries()).map(([productItemId, quantity]) => ({ productItemId, quantity }));
        setGraftsQtyList(graftList);
        setSizeQtyRows(graftList.map((item) => ({
          key: `${item.productItemId}`,
          productItemId: item.productItemId,
          quantity: item.quantity,
        })));
      }
    };

    hydrateOutsideOrderEdit();
  }, [dispatch, outsideOrderEditOrderId, setValue]);

  useEffect(() => {
    if (!isReorder || isOutsideOrderEdit || !orderDetails || !isOutsideOrderDetails(orderDetails)) return;
    if (outsidePlatformReorderHydrated.current) return;

    outsidePlatformReorderHydrated.current = true;
    const info = orderDetails.orderInfo ?? {};
    const normalizedOrderDetails = normalizeReorderOrderDetails(orderDetails);
    const practiceProductId = normalizedOrderDetails.practiceProductId;

    if (info.providerEntryType !== "MANUAL") {
      const providerId = getReorderProviderId(orderDetails);
      if (providerId != null && providerId !== "") {
        setValue("provider", Number(providerId));
      }
    }

    if (info.shippingMethodEntryType === "PLATFORM" && info.shippingMethodId != null && info.shippingMethodId !== "") {
      setValue("requestedShippingMethod", String(info.shippingMethodId));
    }

    if (info.shippingAddressEntryType === "PLATFORM" && info.shippingAddressId != null && info.shippingAddressId !== "") {
      setValue("facilityAddress", Number(info.shippingAddressId));
      setSelectedLocation(String(info.shippingAddressId));
    }

    if (practiceProductId != null && practiceProductId !== "") {
      const numericPracticeProductId = Number(practiceProductId);
      setValue("product", numericPracticeProductId);
      setSelectedProduct({
        label: orderDetails.productName ?? info.productName ?? "",
        value: orderDetails.productId,
        practiceProductId: numericPracticeProductId.toString(),
      });
    }

    if (orderDetails.orderItems?.length) {
      const quantityMap = new Map<number, number>();
      orderDetails.orderItems.forEach((item: OrderItemDTO) => {
        if (!item.productItemId) return;
        quantityMap.set(item.productItemId, (quantityMap.get(item.productItemId) || 0) + (Number(item.quantity) || 0));
      });
      const graftList = Array.from(quantityMap.entries()).map(([productItemId, quantity]) => ({ productItemId, quantity }));
      graftList.forEach(({ productItemId, quantity }) => {
        setValue(`graftQty.${productItemId}` as any, String(quantity));
      });
      setGraftsQtyList(graftList);
      setSizeQtyRows(graftList.map((item) => ({
        key: `${item.productItemId}`,
        productItemId: item.productItemId,
        quantity: item.quantity,
      })));
    }

    orderItemsLoaded.current = true;

    setOrder((prev) => ({
      ...prev,
      orderDetails: normalizedOrderDetails,
    }));
    setIsDisableProducts(false);
  }, [isReorder, isOutsideOrderEdit, orderDetails, setValue]);

  useEffect(() => {
    if (!isReorder || isOutsideOrderEdit || !orderDetails || isOutsideOrderDetails(orderDetails)) return;

    const normalizedOrderDetails = normalizeReorderOrderDetails(orderDetails);
    const providerId = getReorderProviderId(normalizedOrderDetails);
    if (providerId == null || providerId === "") return;

    const currentProvider = getValues("provider");
    if (currentProvider === providerId) return;

    setValue("provider", providerId);
  }, [isReorder, isOutsideOrderEdit, orderDetails, getValues, setValue]);

  useEffect(() => {
    if (orderDetails?.productId) {
      productItemByIdDirectOrder(orderDetails.productId);
      setIsDisableProducts(false);
    };
    // Consolidated: fetchPracticeById will be called by the watchedPracticeDropdown effect
    // No need for separate setPracticeFromIVR call here
    setOrder({
      orderType: orderType,
      orderDetails: isReorder && orderDetails ? normalizeReorderOrderDetails(orderDetails) : orderDetails,
      orderDate: receivedOrderDate,
    });
    if (
      orderType === ORDERTYPE.ORDERED_FROM_PATIENT_LIST ||
      orderType === ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR ||
      orderType === ORDERTYPE.PAST_ORDER
    ) {
      setIsDisableProducts(false);
    }
    setOrderData(orderDetails)
    setUserRole();
    if (orderType !== ORDERTYPE.DIRECT_ORDER && canSeePracticeDropdown) {
      fetchPracticeListByUserRole();
    }
    dispatch(fetchProductsByUserRoleData(true));
  }, []);

  const isProductRequiredOrderType = useMemo(() => 
    order?.orderType === ORDERTYPE.CONSIGNMENT_ORDER ||
    order?.orderType === ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR ||
    order?.orderType === ORDERTYPE.PAST_ORDER ||
    order?.orderType === ORDERTYPE.ORDERED_FROM_PATIENT_LIST,
  [order?.orderType]);

  //Unmount
  useEffect(() => () => {
    dispatch(resetPracticeInfoObject());
    dispatch(resetPracticeByUserRole());
    dispatch(updatePracticeProductsInfoObject([]));
    outsideOrderEditBaseline.current = null;
  }, []);

  const setOrderData = (orderDetails: any) => {
    if (orderDetails?.orderId) {
      setValue('po', orderDetails?.poNumber);
      setValue('dateOfService', orderDetails?.dateOfService);
      setValue("requestedDeliveryDate", orderDetails?.requestedDeliveryDate);
    }
  };

  useEffect(() => {
    if (receivedOrderDate !== null && receivedOrderDate !== undefined && location?.state?.practiceId !== null && location?.state?.practiceId !== undefined)
      dispatch(fetchActiveLocationsForGivenDate({
        practiceId: String(location?.state?.practiceId),
        effectiveDate: receivedOrderDate
      }));
  }, [receivedOrderDate, location?.state?.practiceId])

  useEffect(() => {
    if (isPracticeUser) {
      setIsDisableProducts(false);
    };
  }, [isPracticeUser]);

  const setUserRole = () => {
    if (userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE) {
      setIsPracticeUser(true);
    }

    if (userInfo?.entityName === USER_ROLE_ENTITY_NAMES.ADMIN || userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE || userInfo?.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE) {
      setIsMultiPracticeUser(true);
    };
  }

  const productItemByIdDirectOrder = async (selectedOrderId: number) => {
    const onlyActiveRecords = true;
    const response = await dispatch(fetchProductItemsByProductIdForOrder({ productId: selectedOrderId, onlyActiveRecords }));
    if (response.meta.requestStatus === 'fulfilled') {
      setProductIVR(response?.payload);
    };
  };

  const productItemByIdConsignmentOrder = async (selectedProductId: any) => {
    const onlyActiveRecords = true;
    const response = await dispatch(fetchProductItemsByProductIdForOrder({ productId: selectedProductId, onlyActiveRecords }));
    if (response.meta.requestStatus === 'fulfilled') {
      return response.payload;
    };
  };

  const setOrderItemsData = async (orderDetails: any) => {
    const isManualOutsideProductEdit =
      isOutsideOrderEdit &&
      isManualEntryType(orderDetails?.orderInfo?.productEntryType);

    if (orderDetails?.orderId && !orderItemsLoaded.current && !isManualOutsideProductEdit && !(isReorder && isOutsideOrderDetails(orderDetails))) {
      if (outsideOrderEditUserModified.current) return;
      orderItemsLoaded.current = true;
      const response = await dispatch(getOrderItemsByOrderId(orderDetails.orderId));
      const orderItemList: OrderItemDTO[] = response?.payload?.data;

      if (!isManualOutsideProductEdit) {
        mergeOutsideEditPlatformOrderItemPrices(orderItemList);
      }

      // Aggregate quantities by product_item_id to handle multiple order items for same product
      const quantityMap = new Map<number, number>();
      orderItemList.forEach(orderItem => {
        const currentQty = quantityMap.get(orderItem.productItemId) || 0;
        quantityMap.set(orderItem.productItemId, currentQty + orderItem.quantity);
      });

      // Set aggregated quantities
      let graftList: GraftQty[] = [];
      quantityMap.forEach((quantity, productItemId) => {
        setValue(`graftQty.${productItemId}`, quantity.toString());
        graftList.push({
          productItemId: productItemId,
          quantity: quantity
        });
      });

      setGraftsQtyList(graftList);
      // Reorder / edit order: drive Sizes & Quantity rows from API so dropdowns show prior selections
      // (hydration effect alone can miss this if the user already opened placeholder rows).
      if (graftList.length > 0) {
        setSizeQtyRows(
          graftList.map((g) => ({
            key: `${g.productItemId}`,
            productItemId: g.productItemId,
            quantity: Math.max(0, Number(g.quantity) || 0),
          }))
        );
      }
    }
  };

  useEffect(() => {
    if (productIVR) {
      setListData(() => {
        const newValues = [...productIVR]
          .sort((a, b) => Number(a.units) - Number(b.units))
          .map(productItem => ({
            graftsNeeded: productItem.size,
            id: productItem.productItemId,
          }))
        return newValues;
      });
      setOrderItemsData(order?.orderDetails)
    } else if (selectedProduct) {
      productItemByIdConsignmentOrder(selectedProduct.value).then((data) => {
        // Keep product item details for order summary pricing in non-IVR flows as well.
        setProductIVR(data || []);
        setListData(() => {
          const newValues = [...(data || [])]
            .sort((a: { size: string; productItemId: number; units: number }, b: { size: string; productItemId: number; units: number }) => a.units - b.units)
            .map((productItem: { size: string; productItemId: number; units: number, productItemDescription: string | null }) => ({
              graftsNeeded: productItem.size,
              id: productItem.productItemId,
            }));
          return newValues;
        });
      });
    };
  }, [productIVR, selectedProduct]);

  // CS-1766 - Fetch product shipping config for order when product is selected.
  // When the user manually changes the product (selectedProduct is set), always prefer
  // selectedProduct?.value so that reorder → product-change correctly re-fetches configs
  // for the newly chosen product instead of staying locked on the original order's productId.
  const resolvedProductIdForShipping = useMemo(() => {
    if (isPastOrder && outsideManual.product) return null;
    if (selectedProduct?.value != null) {
      return selectedProduct.value;
    }
    if (isPastOrder && !outsideManual.product && !watchedProductField) {
      return null;
    }
    if (isOutsideOrderEdit && order?.orderDetails?.productId != null) {
      return order.orderDetails.productId;
    }
    if (order?.orderType === ORDERTYPE.DIRECT_ORDER) {
      return order?.orderDetails?.productId ?? null;
    }
    return null;
  }, [isPastOrder, outsideManual.product, selectedProduct?.value, watchedProductField, isOutsideOrderEdit, order?.orderType, order?.orderDetails?.productId]);

  // CS-1997 - Pass the practice so the backend zeroes shipping rates for free-shipping practices.
  const practiceIdForShipping = watch("practiceDropdown") || order?.orderDetails?.practiceId;

  useEffect(() => {
    if (resolvedProductIdForShipping) {
      dispatch(fetchProductShippingConfigForOrder({
        productId: Number(resolvedProductIdForShipping),
        practiceId: practiceIdForShipping ? Number(practiceIdForShipping) : undefined,
      }));
    }
  }, [resolvedProductIdForShipping, practiceIdForShipping, dispatch]);

  // CS-1845 START - Fetch product config and derive upfront pricing availability.
  const getUpfrontPricingFromRules = (rules: OrderProductConfigRule[] = []): UpfrontPricingConfig => {
    const purchaseAndBillEnabled = rules.some((rule) =>
      rule.configType === PRODUCT_CONFIG_TYPE_PURCHASE_AND_BILL &&
      rule.configCode === PRODUCT_CONFIG_CODE_ENABLE &&
      rule.value === PRODUCT_CONFIG_VALUE_YES
    );
    const upfrontRule = rules.find((rule) =>
      rule.configType === PRODUCT_CONFIG_TYPE_UPFRONT_PRICING &&
      rule.configCode === PRODUCT_CONFIG_CODE_ENABLE
    );
    const discountValue = Number(upfrontRule?.metadata?.[UPFRONT_DISCOUNT_METADATA_KEY] ?? 0);

    return {
      enabled: purchaseAndBillEnabled && upfrontRule?.value === PRODUCT_CONFIG_VALUE_YES,
      upfrontDiscount: Number.isFinite(discountValue) ? discountValue : 0,
      purchaseAndBill: purchaseAndBillEnabled,
    };
  };

  useEffect(() => {
    const fetchUpfrontPricingConfig = async () => {
      if (!resolvedProductIdForShipping) {
        setUpfrontPricingConfig({ enabled: false, upfrontDiscount: 0, purchaseAndBill: false });
        return;
      }

      try {
        const response = await getProductConfigByProduct(Number(resolvedProductIdForShipping));
        const rules: OrderProductConfigRule[] = response?.data?.data || [];
        setUpfrontPricingConfig(getUpfrontPricingFromRules(rules));
      } catch {
        setUpfrontPricingConfig({ enabled: false, upfrontDiscount: 0, purchaseAndBill: false });
      }
    };

    fetchUpfrontPricingConfig();
  }, [resolvedProductIdForShipping]);
  // CS-1845 END

  // CS-1766 - Commented out: codetable-based shipping method logic replaced by product-specific API
  // useEffect(() => {
  //   if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
  //     //CS-1567 START
  //     // Determine the name to use for shipping method lookup
  //     let productName: number | string | undefined;
  //     
  //     if (order?.orderType === ORDERTYPE.DIRECT_ORDER) {
  //       productName = order?.orderDetails?.productName;
  //     } else if (selectedProduct) {
  //       productName = selectedProduct.label;
  //     }
  //
  //     let productShippingMethods: CodeTable[] = [];
  //     if (productName) {
  //       const productSpecificTableName = `customShipping_${productName}`;
  //       productShippingMethods = getCodeTableValuesByTableName(allCodeTablesValuesMap, productSpecificTableName);
  //     }
  //
  //     if (productShippingMethods && productShippingMethods.length > 0) {
  //       setShippingMethods(productShippingMethods);
  //     } else {
  //       setShippingMethods(getCodeTableValuesByTableName(allCodeTablesValuesMap, "shippingMethod"));
  //     }
  //     //
  //   }
  // }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus, selectedProduct, order]);

  useEffect(() => {
    // Don't overwrite provider-scoped products when coming from quick action or explore market
    const locationPracticeId = location?.state?.practiceId;
    const locationProviderId = location?.state?.providerId ?? location?.state?.practiceProviderId;
    const locationProductId = location?.state?.productId;
    const shouldUseProviderScopedProductsOnInit = !!(locationPracticeId && locationProviderId && locationProductId);

    if (shouldUseProviderScopedProductsOnInit) {
      return;
    }

    if (practiceByUserRole && practiceByUserRole.length > 0 && isPracticeUser) {
      dispatch(fetchPracticeProductsData(practiceByUserRole[0].practiceId))
    }
  }, [practiceByUserRole, location?.state])

  // CS-1732: practice-products API may return one object or an array
  const practiceProductsList = useMemo(() => {
    if (!practiceProductsInfo) return [];
    const rawList = Array.isArray(practiceProductsInfo) ? practiceProductsInfo : [practiceProductsInfo as any];
    const uniqueByPracticeProductId = new Map<string, any>();
    rawList.forEach((product: any) => {
      const key = String(product?.practiceProductId ?? "");
      if (!key) return;
      if (!uniqueByPracticeProductId.has(key)) {
        uniqueByPracticeProductId.set(key, product);
      }
    });
    return Array.from(uniqueByPracticeProductId.values());
  }, [practiceProductsInfo]);

  const selectedFullProduct = useMemo(() => {
    const id = selectedProduct?.value || order?.orderDetails?.productId;
    // Fallback to practiceProductsList if productsByUserRole doesn't have it (or hasn't loaded)
    return productsByUserRole?.find(p => String(p.productId) === String(id)) ||
      practiceProductsList?.find((p: any) => String(p.productId) === String(id));
  }, [productsByUserRole, practiceProductsList, selectedProduct, order?.orderDetails?.productId]);
  const watchedPracticeDropdown = watch("practiceDropdown");
  useEffect(() => {
    if (!isPastOrder || outsideManual.practice || !watchedPracticeDropdown) {
      if (isPastOrder) {
        dispatch(resetActivePracticeLocationsInfo());
      }
      return;
    }
    const effectiveDate = getValues("orderDate") || order?.orderDate || dayjs().format("MM/DD/YYYY");
    dispatch(fetchActiveLocationsForGivenDate({
      practiceId: String(watchedPracticeDropdown),
      effectiveDate,
    }));
  }, [dispatch, isPastOrder, outsideManual.practice, watchedPracticeDropdown, order?.orderDate, getValues]);

  useEffect(() => {
    return () => {
      dispatch(resetActivePracticeLocationsInfo());
    };
  }, [dispatch]);

  const sameValue = (a: any, b: any) =>
    a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);

  const resolveOptionValue = (
    options: { value: string | number }[],
    id: string | number | null | undefined
  ) => {
    if (id == null || id === "") return undefined;
    return options.find((option) => String(option.value) === String(id))?.value;
  };
  const normalizePracticeProducts = (response: any) => {
    const products = response?.payload?.data;
    return Array.isArray(products) ? products : products ? [products] : [];
  };

  const applyProductSelection = (products: any[]) => {
    if (!products || products.length === 0) return;

    const selectedProviderId = isPastOrder
      ? (getValues as any)("providerDropdown")
      : getValues("provider");
    const currentPracticeProductId = getValues("product");

    if (outsideOrderEditUserModified.current) {
      if (!currentPracticeProductId) return;
      const currentProduct = products.find((prod: any) =>
        sameValue(prod.practiceProductId, currentPracticeProductId)
      );
      if (currentProduct) {
        setValue("product", currentProduct.practiceProductId);
        setSelectedProduct({
          label: currentProduct.productName,
          value: currentProduct.productId,
          practiceProductId: currentProduct.practiceProductId
        });
      }
      return;
    }

    const currentProduct = products.find((prod: any) =>
      sameValue(prod.practiceProductId, currentPracticeProductId)
    );

    if (currentProduct) {
      setValue("product", currentProduct.practiceProductId);
      setSelectedProduct({
        label: currentProduct.productName,
        value: currentProduct.productId,
        practiceProductId: currentProduct.practiceProductId
      });
      return;
    }

    const preferredPracticeProductId =
      location?.state?.practiceProductId ??
      order?.orderDetails?.practiceProductId ??
      order?.orderDetails?.orderInfo?.practiceProductId ??
      order?.orderDetails?.orderItems?.[0]?.practiceProductId;
    const preferredProductId = location?.state?.productId ?? order?.orderDetails?.productId;

    let matchingProduct = preferredPracticeProductId
      ? products.find((prod: any) => sameValue(prod.practiceProductId, preferredPracticeProductId))
      : null;

    if (!matchingProduct && preferredProductId) {
      matchingProduct = products.find((prod: any) => {
        const isSameProduct = sameValue(prod.productId, preferredProductId);
        if (!isSameProduct) return false;
        if (selectedProviderId == null || selectedProviderId === "") return true;
        if (prod.practiceProviderId == null || prod.practiceProviderId === "") return true;
        return sameValue(prod.practiceProviderId, selectedProviderId);
      });
    }

    if (!matchingProduct && products.length === 1) {
      matchingProduct = products[0];
    }

    if (!matchingProduct) return;

    setValue("product", matchingProduct.practiceProductId);
    setSelectedProduct({
      label: matchingProduct.productName,
      value: matchingProduct.productId,
      practiceProductId: matchingProduct.practiceProductId
    });
  };

  // CS-1732: Pre-select product from explore market when products are fetched
  useEffect(() => {
    if (isPastOrder && (outsideManual.practice || !watchedPracticeDropdown)) return;
    if (isPastOrder && outsideManual.provider) return;
    if (outsideOrderEditUserModified.current) return;
    if (practiceProductsList.length > 0) {
      applyProductSelection(practiceProductsList);
    }
  }, [practiceProductsList, order, location?.state?.practiceProductId, location?.state?.productId, isPastOrder, outsideManual.practice, outsideManual.provider, watchedPracticeDropdown]);

  useEffect(() => {
    if (!isReorder || outsideOrderEditUserModified.current) return;
    if (!order?.orderDetails?.practiceId) return;

    const info = order.orderDetails.orderInfo ?? {};
    if (info.providerEntryType === "MANUAL") return;

    const providerId = getReorderProviderId(order.orderDetails);
    if (providerId == null || providerId === "") return;

    const providerField = isPastOrder ? "providerDropdown" : "provider";
    const currentProvider = getValues(providerField as any);
    if (currentProvider == null || currentProvider === "") {
      setValue(providerField as any, Number(providerId));
    }

    if (practiceProductsList.length > 0) return;

    const loadReorderProducts = async () => {
      const response = await dispatch(fetchPracticeProductsDataUsingDateOfServiceAndProvider({
        practiceId: Number(order.orderDetails.practiceId),
        providerId: Number(providerId),
        moduleName: "CREATE_ORDER",
        dateOfService: order.orderDate || receivedOrderDate || ""
      }));

      if (response?.meta?.requestStatus === "fulfilled") {
        applyProductSelection(normalizePracticeProducts(response));
      }
    };

    loadReorderProducts();
  }, [
    isReorder,
    order?.orderDetails,
    order.orderDate,
    receivedOrderDate,
    practiceProductsList.length,
    isPastOrder,
    dispatch,
    getValues,
    setValue,
  ]);

  const dateOfService = watch('dateOfService');
  const watchedPracticeProductId = watch("product");
  const watchedProviderId = isPastOrder ? watch("providerDropdown" as any) : watch("provider");
  const watchedShippingMethod = watch('requestedShippingMethod');

  useEffect(() => {
    if (!isReorder || outsideOrderEditUserModified.current) return;
    if (!practiceProductsList.length) return;
    applyProductSelection(practiceProductsList);
  }, [isReorder, practiceProductsList, watchedProviderId, order?.orderDetails?.practiceProductId, order?.orderDetails?.orderInfo?.practiceProductId]);
  
  useEffect(() => {
    const locationPracticeId = location?.state?.practiceId;
    const locationProviderId = location?.state?.providerId ?? location?.state?.practiceProviderId;
    const locationProductId = location?.state?.productId;
    const shouldUseProviderScopedProductsOnInit = !!(locationPracticeId && locationProviderId && locationProductId);

    if (shouldUseProviderScopedProductsOnInit) return;
    if (isPastOrder && (outsideManual.practice || !watchedPracticeDropdown)) return;

    const selectedPracticeId = watchedPracticeDropdown;
    if (canSeePracticeDropdown && !selectedPracticeId) {
      return;
    }

    const practiceId = selectedPracticeId || (!canSeePracticeDropdown ? PracticeSaveInfo?.practiceId : null);

    if (practiceId) {
      // const response = dispatch(fetchPracticeProductsData(practiceId));
      // CS-1240
      dispatch(fetchPracticeProductsDataWithModule({
        practiceId: Number(practiceId),
        moduleName: "CREATE_ORDER"
      }));
    }
  }, [watchedPracticeDropdown, location.state?.orderType, PracticeSaveInfo, canSeePracticeDropdown]);

  useEffect(() => {
    const locationPracticeId = location?.state?.practiceId;
    const locationProviderId = location?.state?.providerId ?? location?.state?.practiceProviderId;
    const locationProductId = location?.state?.productId;
    const shouldUseProviderScopedProductsOnInit = !!(locationPracticeId && locationProviderId && locationProductId);

    if (!shouldUseProviderScopedProductsOnInit || initialProviderScopedProductsLoaded.current) return;

    const loadInitialProviderScopedProducts = async () => {
      initialProviderScopedProductsLoaded.current = true;
      setValue("practiceDropdown", locationPracticeId);
      setValue("provider", locationProviderId);

      const response = await dispatch(fetchPracticeProductsDataUsingDateOfServiceAndProvider({
        practiceId: Number(locationPracticeId),
        providerId: Number(locationProviderId),
        moduleName: "CREATE_ORDER",
        dateOfService: order.orderDate || location?.state?.orderDate || ""
      }));

      if (response?.meta?.requestStatus === "fulfilled") {
        const fetchedProducts = normalizePracticeProducts(response);
        applyProductSelection(fetchedProducts);
      }
    };

    loadInitialProviderScopedProducts();
  }, [
    dispatch,
    location?.state?.practiceId,
    location?.state?.providerId,
    location?.state?.practiceProviderId,
    location?.state?.productId,
    location?.state?.orderDate,
    order.orderDate,
    setValue
  ]);

  const fetchPracticeListByUserRole = async () => {
    const response = await dispatch(fetchAllUserRoles());
    if (response?.meta?.requestStatus === 'fulfilled') {
      if (response?.payload) {
        const userRoles = response.payload;
        const role = userRoles?.find((role: UserRole) => role.roleName === userInfo.userRole);
        if (role) {
          dispatch((getPracticesByUserRole(false)))
        };
      };
    };
  };

  // CS-1766 - Parse shipping cost from product shipping config API response
  const shippingCost = useMemo(() => {
    if (!watchedShippingMethod || !productShippingConfigForOrder?.length) return null;
    const method = productShippingConfigForOrder.find(
      (config: any) => String(config.shippingConfigId) === String(watchedShippingMethod)
    );
    if (!method) return null;
    return method.rate > 0 ? Number(method.rate) : null;
  }, [watchedShippingMethod, productShippingConfigForOrder]);

  const shippingLabel = useMemo(() => {
    if (!watchedShippingMethod || !productShippingConfigForOrder?.length) return null;
    const method = productShippingConfigForOrder.find(
      (config: any) => String(config.shippingConfigId) === String(watchedShippingMethod)
    );
    if (!method) return null;
    const rate = method.rate > 0 ? `$${Number(method.rate).toFixed(2)}` : "";
    return rate ? `${rate} (${method.name})` : method.name;
  }, [watchedShippingMethod, productShippingConfigForOrder]);

  // CS-1766 - Free shipping zeroes the charge, so the summary shows the price it would have been,
  // struck through and marked as waived. baseRate is the price before the waiver.
  const shippingWaivedAmount = useMemo(() => {
    if (!watchedShippingMethod || !productShippingConfigForOrder?.length) return null;
    const method = productShippingConfigForOrder.find(
      (config: any) => String(config.shippingConfigId) === String(watchedShippingMethod)
    );
    if (!method) return null;
    const charged = Number(method.rate ?? 0);
    const base = Number(method.baseRate ?? 0);
    return charged === 0 && base > 0 ? base : null;
  }, [watchedShippingMethod, productShippingConfigForOrder]);

  const resolvedShippingCost = useMemo(() => {
    if (isPastOrder && (outsideManual.shippingMethod || outsideManual.product)) {
      const val = parseFloat(watchedOutsideShipPrice) || 0;
      return val > 0 ? val : null;
    }
    return shippingCost;
  }, [isPastOrder, outsideManual.shippingMethod, outsideManual.product, watchedOutsideShipPrice, shippingCost]);

  const resolvedShippingLabel = useMemo(() => {
    if (isPastOrder && (outsideManual.shippingMethod || outsideManual.product)) {
      const val = parseFloat(watchedOutsideShipPrice) || 0;
      return val > 0 ? `$${val.toFixed(2)}` : null;
    }
    return shippingLabel;
  }, [isPastOrder, outsideManual.shippingMethod, outsideManual.product, watchedOutsideShipPrice, shippingLabel]);

  const selectedPracticeProductInfo = useMemo(() => {
    if (!practiceProductsList.length) return null;

    const selectedProductId = selectedProduct?.value ?? order?.orderDetails?.productId;
    const selectedPracticeProductId =
      selectedProduct?.practiceProductId ??
      watchedPracticeProductId ??
      order?.orderDetails?.practiceProductId ??
      order?.orderDetails?.orderInfo?.practiceProductId ??
      order?.orderDetails?.orderItems?.[0]?.practiceProductId ??
      location?.state?.practiceProductId;

    let matched = practiceProductsList.find((prod: any) =>
      sameValue(prod.practiceProductId, selectedPracticeProductId)
    );

    if (!matched && selectedProductId != null) {
      matched = practiceProductsList.find((prod: any) => {
        const sameProduct = sameValue(prod.productId, selectedProductId);
        if (!sameProduct) return false;
        if (watchedProviderId == null || watchedProviderId === "") return true;
        // GROUP-level agreements have no practiceProviderId; they apply regardless of selected provider.
        if (prod.practiceProviderId == null || prod.practiceProviderId === "") return true;
        return sameValue(prod.practiceProviderId, watchedProviderId);
      });
    }

    return matched ?? null;
  }, [practiceProductsList, selectedProduct, order, watchedPracticeProductId, watchedProviderId, location?.state?.practiceProductId]);

  const resolvedOrderPricing = useMemo(() => {
    const extendedPriceValue =
      selectedPracticeProductInfo?.extendedPrice ??
      selectedPracticeProductInfo?.practiceExtendedPrice ??
      validPP?.extendedPrice ??
      null;

    const normalizedExtendedPrice =
      extendedPriceValue !== null && extendedPriceValue !== undefined && extendedPriceValue !== ""
        ? Number(extendedPriceValue)
        : null;

    const skuAllowablePrices =
      selectedPracticeProductInfo?.skuAllowablePrices ??
      selectedPracticeProductInfo?.skuExtendedPrices ??
      null;

    const hasSkuList = Array.isArray(skuAllowablePrices) && skuAllowablePrices.length > 0;
    const hasSku =
      (selectedPracticeProductInfo?.hasSKUPrice ?? selectedPracticeProductInfo?.hasSku ?? false) ||
      hasSkuList;

    const upfrontPriceValue =
      selectedPracticeProductInfo?.upfrontPrice ??
      selectedPracticeProductInfo?.practiceUpfrontPrice ??
      validPP?.upfrontPrice ??
      null;

    const normalizedUpfrontPrice =
      upfrontPriceValue !== null && upfrontPriceValue !== undefined && upfrontPriceValue !== ""
        ? Number(upfrontPriceValue)
        : null;

    // Get upfrontDiscount from API response.
    // For SKU products: read from the first SKU entry that has a valid upfrontDiscount.
    // For flat-price products: read from the top-level field.
    let normalizedUpfrontDiscount = 0;

    if (hasSku && hasSkuList) {
      // Find the first SKU entry with a non-null upfrontDiscount
      const skuWithDiscount = skuAllowablePrices!.find(
        (sku: any) => sku.upfrontDiscount !== null && sku.upfrontDiscount !== undefined && sku.upfrontDiscount !== ""
      );
      if (skuWithDiscount) {
        normalizedUpfrontDiscount = Number(skuWithDiscount.upfrontDiscount);
      } else {
        // Derive from first SKU that has both extendedPrice and upfrontPrice
        const skuWithPrices = skuAllowablePrices!.find(
          (sku: any) => sku.extendedPrice && sku.upfrontPrice && sku.extendedPrice > sku.upfrontPrice
        );
        if (skuWithPrices) {
          normalizedUpfrontDiscount = Math.round(
            ((skuWithPrices.extendedPrice - skuWithPrices.upfrontPrice) / skuWithPrices.extendedPrice) * 100
          );
        }
      }
    } else {
      const upfrontDiscountValue =
        (selectedPracticeProductInfo as any)?.upfrontDiscount ??
        (validPP as any)?.upfrontDiscount ??
        null;
      if (upfrontDiscountValue !== null && upfrontDiscountValue !== undefined && upfrontDiscountValue !== "") {
        normalizedUpfrontDiscount = Number(upfrontDiscountValue);
      } else if (normalizedExtendedPrice && normalizedUpfrontPrice && normalizedExtendedPrice > normalizedUpfrontPrice) {
        // Derive from flat prices when field is absent
        normalizedUpfrontDiscount = Math.round(
          ((normalizedExtendedPrice - normalizedUpfrontPrice) / normalizedExtendedPrice) * 100
        );
      }
    }

    return {
      extendedPrice: hasSku ? null : normalizedExtendedPrice,
      upfrontPrice: hasSku ? null : normalizedUpfrontPrice,
      upfrontDiscount: normalizedUpfrontDiscount,
      hasSku,
      skuAllowablePrices: hasSku ? skuAllowablePrices : null,
    };
  }, [selectedPracticeProductInfo, validPP]);

  /** True when practice product pricing includes a non-null upfront price or discount. Aligns with create-order validation. */
  const practiceProductUpfrontReady = useMemo(() => {
    const { hasSku, skuAllowablePrices, upfrontPrice, upfrontDiscount } = resolvedOrderPricing;
    if (hasSku) {
      if (!Array.isArray(skuAllowablePrices) || skuAllowablePrices.length === 0) {
        return false;
      }
      return skuAllowablePrices.every((sku: any) => {
        const hasUpPrice = sku.upfrontPrice !== null && sku.upfrontPrice !== undefined && String(sku.upfrontPrice).trim() !== "";
        const hasUpDisc = sku.upfrontDiscount !== null && sku.upfrontDiscount !== undefined && String(sku.upfrontDiscount).trim() !== "";
        return hasUpPrice || hasUpDisc;
      });
    }
    return (upfrontPrice !== null && upfrontPrice !== undefined) || (upfrontDiscount > 0);
  }, [resolvedOrderPricing]);

  // CS-2070 - The upfront discount only applies when upfront pricing is enabled AND the practice product
  // carries an upfront price/discount; this drives the "Discount" savings line and the discounted Pay Now total.
  const hasUpfrontDiscount = upfrontPricingConfig.enabled && practiceProductUpfrontReady;

  // CS-2070 - The Pay Now / Pay Later toggle shows for any Purchase-and-Bill product, with or without a discount.
  const showUpfrontOrderUi = upfrontPricingConfig.purchaseAndBill;

  // CS-2070 - Without an applicable upfront discount, drop per-SKU upfront values so the card charges the
  // regular total on Pay Now (no discount line item).
  const cardSkuAllowablePrices = useMemo(() => {
    const skus = resolvedOrderPricing.skuAllowablePrices;
    if (hasUpfrontDiscount || !Array.isArray(skus)) return skus;
    return skus.map((sku: any) => ({ ...sku, upfrontPrice: null, upfrontDiscount: null }));
  }, [resolvedOrderPricing.skuAllowablePrices, hasUpfrontDiscount]);

  // CS-1845 - orderQuantities: used by LiveOrderSummary for per-item price lookup.
  const orderQuantities = useMemo(() => (
    graftsQtyList?.reduce((acc, g) => {
      acc[g.productItemId] = Number(g.quantity);
      return acc;
    }, {} as { [id: number]: number }) ?? {}
  ), [graftsQtyList]);

  const summaryProductItems = useMemo(
    () =>
      productIVR?.map((p) => ({
        productItemId: p.productItemId,
        size: p.size,
        productItemDescription: p.productItemDescription,
        units: p.units,
      })) ?? [],
    [productIVR]
  );

  const skuPriceLookup = useMemo(() => {
    const { skuAllowablePrices } = resolvedOrderPricing;
    if (!skuAllowablePrices || skuAllowablePrices.length === 0) return null;
    const byProductItemId: { [id: number]: number } = {};
    const bySize: { [size: string]: number } = {};
    skuAllowablePrices.forEach((entry: any) => {
      const price = entry.extendedPrice ?? entry.allowablePrice ?? 0;
      if (entry.productItemId != null) byProductItemId[entry.productItemId] = price;
      if (entry.size) bySize[entry.size] = price;
    });
    return (Object.keys(byProductItemId).length > 0 || Object.keys(bySize).length > 0)
      ? { byProductItemId, bySize }
      : null;
  }, [resolvedOrderPricing]);

  const getRowUnitPrice = useCallback((productItemId: number | null): number | null => {
    if (productItemId == null) return null;

    if (isOutsideOrderEdit && !outsideManual.product) {
      const savedPrice = outsideEditPlatformOrderItemPrices[productItemId];
      if (savedPrice != null) return savedPrice;
    }

    if (resolvedOrderPricing.hasSku && skuPriceLookup) {
      const itemInfo = productIVR?.find(p => p.productItemId === productItemId);
      const price = skuPriceLookup.byProductItemId[productItemId] ??
                    (itemInfo?.size ? skuPriceLookup.bySize[itemInfo.size] : undefined);
      if (price !== undefined) return price;
    }
    if (!resolvedOrderPricing.hasSku && resolvedOrderPricing.extendedPrice != null) {
      const itemInfo = productIVR?.find(p => p.productItemId === productItemId);
      const units = Number(itemInfo?.units) || 1;
      return resolvedOrderPricing.extendedPrice * units;
    }
    return null;
  }, [isOutsideOrderEdit, outsideManual.product, outsideEditPlatformOrderItemPrices, resolvedOrderPricing, skuPriceLookup, productIVR]);

  // const summaryProductName = useMemo(
  //   () => selectedProduct?.label ?? order?.orderDetails?.productName ?? "",
  //   [order?.orderDetails?.productName, selectedProduct?.label]
  // );
  const summaryProductName = useMemo(() => {
    if (outsideManual.product) return watch("productManual" as any) || "";
    return selectedProduct?.label ?? order?.orderDetails?.productName ?? "";
  }, [outsideManual.product, watch("productManual" as any), selectedProduct, order?.orderDetails?.productName]);

  const summaryManufacturerName = useMemo(() => {
    const fromCatalog =
      (selectedFullProduct as { manufacturerName?: string } | undefined)?.manufacturerName ??
      (selectedPracticeProductInfo as { manufacturerName?: string } | undefined)?.manufacturerName;
    if (fromCatalog != null && String(fromCatalog).trim() !== "") return String(fromCatalog).trim();
    const fromOrder = (order?.orderDetails as { manufacturerName?: string } | undefined)?.manufacturerName;
    if (fromOrder != null && String(fromOrder).trim() !== "") return String(fromOrder).trim();
    return null;
  }, [selectedFullProduct, selectedPracticeProductInfo, order?.orderDetails]);

  const stripeInvoiceData = useMemo(() => {
    const normalizeStripeConfig = (raw: unknown) => {
      let mfrConfig = raw;
      if (mfrConfig && typeof mfrConfig === "string") {
        mfrConfig = parseJsonSafely<unknown>(mfrConfig) ?? mfrConfig;
      }
      if (mfrConfig && typeof mfrConfig === "object" && Array.isArray((mfrConfig as { payment_method?: unknown }).payment_method)) {
        const cfg = mfrConfig as { payment_method: string[] };
        return { ...cfg, payment_method: cfg.payment_method.join(",") };
      }
      return mfrConfig;
    };

    if (upfrontOrderResponse != null) {
      const orderId = resolveCreatedOrderId(upfrontOrderResponse);
      if (
        typeof upfrontOrderResponse === "object" &&
        upfrontOrderResponse !== null &&
        "invoiceBalance" in upfrontOrderResponse
      ) {
        return upfrontOrderResponse;
      }
      return {
        orderId,
        invoiceBalance: upfrontCardTotal,
        manufacturerStripeConfig: normalizeStripeConfig(mfrStripeConfig || validPP?.manufacturerStripeConfig),
        ...(typeof upfrontOrderResponse === "object" && upfrontOrderResponse !== null ? upfrontOrderResponse : {}),
      };
    }
    if (pendingUpfrontOrderPayload) {
      return {
        invoiceBalance: upfrontCardTotal,
        manufacturerStripeConfig: normalizeStripeConfig(mfrStripeConfig || validPP?.manufacturerStripeConfig),
      };
    }
    return null;
  }, [upfrontOrderResponse, pendingUpfrontOrderPayload, upfrontCardTotal, validPP, mfrStripeConfig]);


  useEffect(() => {
    const fetchPracticeProduct = async () => {
      // Resolve practice: form dropdown (multi-practice flows), else order/signed-in practice.
      // For DIRECT_ORDER the dropdown is often hidden; do not require it or agreement fetch
      // incorrectly uses the *original* order productId while the user already picked another product.
      const practiceFromForm = getValues("practiceDropdown");
      const practiceId =
        practiceFromForm ||
        order?.orderDetails?.practiceId ||
        PracticeSaveInfo?.practiceId;

      // Always prefer the user's current product selection for agreement / upfront pricing.
      const productId =
        selectedProduct?.value != null && selectedProduct.value !== ""
          ? Number(selectedProduct.value)
          : order?.orderDetails?.productId;

      if (practiceId == null || practiceId === "" || productId == null) {
        return;
      }

      const response = await dispatch(getAgreementByProductIdAndPracticeId({ practiceId, productId }));

      if (response?.meta?.requestStatus === 'fulfilled') {
        const agreementList = response.payload || [];
        // const checkDate = dayjs(getValues('dateOfService')) || dayjs();
        // CS-1341 - Consider Order date for fetching Correct Agreement
        const checkDate = dayjs(order.orderDate) || dayjs();

        const validAgreement = agreementList?.find((agreement: PracticeProductAgreementDTO) => {
          //CS-1510 - Handled logic to check inavtive data === today's date
          return (
            (dayjs(agreement.effectiveDate).isBefore(checkDate) || dayjs(agreement.effectiveDate).isSame(checkDate, 'day')) &&
            (agreement.inactiveDate === null || dayjs(agreement.inactiveDate).isAfter(checkDate) || dayjs(agreement.inactiveDate).isSame(checkDate, 'day'))
          );
        });
        setValidPP(validAgreement);

        // CS-1845 - Fetch Manufacturer Stripe Config if missing
        const mfrId = selectedFullProduct?.manufacturerId || (selectedPracticeProductInfo as any)?.manufacturerId;
        if (mfrId && (!validAgreement?.manufacturerStripeConfig) && !mfrStripeConfig) {
          const mfrResponse: any = await dispatch(fetchManufacturerDataById(Number(mfrId)));
          if (mfrResponse.meta.requestStatus === 'fulfilled') {
            const mfrData = mfrResponse.payload?.data?.data;
            let stripeConfig = mfrData?.stripeConfig || mfrData?.manufacturerStripeConfig || mfrData?.customConfig?.stripeConfig;

            if (stripeConfig && typeof stripeConfig === 'string') {
              stripeConfig = parseJsonSafely<unknown>(stripeConfig) ?? stripeConfig;
            }
            if (stripeConfig && Array.isArray(stripeConfig.payment_method)) {
              stripeConfig.payment_method = stripeConfig.payment_method.join(',');
            }
            if (stripeConfig) {
              setMfrStripeConfig(stripeConfig);
            }
          }
        }
      }
    };
    fetchPracticeProduct();
  }, [order, dateOfService, selectedFullProduct, selectedProduct, PracticeSaveInfo?.practiceId]);


  const onSubmit = async (upfrontPayment = false) => {
    const selectedPracticeProductId = getValues("product");

    if (isProductRequiredOrderType && (!selectedPracticeProductId || !selectedProduct)) {
      let errObj: SingleAlertInfo = {
        message: "Product is required.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errObj));
      return;
    }

    // Pay Now: shipping required when product shipping options exist
    if (
      upfrontPayment &&
      shippingMethodOptions.length > 0 &&
      !getValues("requestedShippingMethod")
    ) {
      const errObj: SingleAlertInfo = {
        message: "Please select a shipping method.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errObj));
      return;
    }

    // CS-2070 - Pay Now is allowed for any Purchase-and-Bill product; when no upfront discount is configured
    // it simply charges the regular total (no discount), so the old "no upfront price configured" block is gone.

    if (graftsQtyList && graftsQtyList.length > 0) {

      if ((order?.orderType === ORDERTYPE.DIRECT_ORDER && !validPP) || (order?.orderType === ORDERTYPE.ORDERED_FROM_PATIENT_LIST && !validPP)) {
        let errObj: SingleAlertInfo = {
          message: `Valid Product Agreement not found for the product "${selectedProduct?.label ?? order?.orderDetails?.productName}".`,
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errObj));
      } else {
        if (upfrontPayment) {
          // CS-1845 - Check if stripe integration is enabled for the manufacturer
          let stripeConfig = mfrStripeConfig || validPP?.manufacturerStripeConfig;

          // Parse stripe config if it's a string
          if (stripeConfig && typeof stripeConfig === 'string') {
            stripeConfig = parseJsonSafely<unknown>(stripeConfig) ?? stripeConfig;
          }

          // Check if stripe integration is enabled
          if (stripeConfig?.stripe_integration === 'Y') {
            const payload = getCreateOrderPayload(true);
            if (payload) {
              setCheckoutError(null);
              setPendingUpfrontOrderPayload(payload);
              setShowStripePaymentTypePopup(true);
            }
          } else {
            // Stripe integration not enabled - show message and proceed with normal order flow
            setShowOptionsPopup({
              message: "Online payment is not enabled for this product. Would you like to create a standard order instead?",
              show: true,
              variant: "info",
              buttons: [{ name: "Yes, Create Order", color: 'primary', onClick: () => createOrder(false) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
            });
          }
        } else {
          setShowOptionsPopup({
            message: "Do you really want to create the order?",
            show: true,
            variant: "confirm",
            buttons: [{ name: "Confirm", color: 'primary', onClick: () => createOrder(false) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
          });
        }
      }
    } else {
      let errObj: SingleAlertInfo = {
        message: "Please enter a quantity for at least one graft.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errObj));
    };
  };

  // CS-1766 - Build shipping method options from product shipping config API
  const shippingMethodOptions = useMemo(() => {
    if (isPastOrder && outsideManual.product) return [];
    if (!resolvedProductIdForShipping) return [];
    if (!productShippingConfigForOrder || productShippingConfigForOrder.length === 0) return [];
    return productShippingConfigForOrder.map((config: any) => {
      const rate = Number(config.rate ?? 0);
      const baseRate = Number(config.baseRate ?? 0);
      // CS-1997 - Free shipping zeroes `rate`, so fall back to baseRate (the real price) — the
      // same amount the summary strikes through as "Waived off".
      const displayRate = rate > 0 ? rate : baseRate;
      const safeRate = Number.isFinite(displayRate) ? displayRate : 0;
      // CS-1766 - A zero charge carries no information, so the bracket is left off entirely
      // (e.g. "Other - Call Support after Posting Order").
      const label = safeRate > 0
        ? `${config.carrier} - ${config.name} ($${safeRate.toFixed(2)})`
        : `${config.carrier} - ${config.name}`;
      return {
        label,
        value: String(config.shippingConfigId),
      };
    });
  }, [productShippingConfigForOrder, isPastOrder, outsideManual.product, resolvedProductIdForShipping]);

  const isShippingRequiredForPayNow =
    showUpfrontOrderUi && sidebarPaymentMode === "now" && shippingMethodOptions.length > 0;

  const shippingMethodRules = useMemo(
    () => ({
      ...(isShippingRequiredForPayNow
        ? { required: "Please select a shipping method." }
        : {}),
      validate: (value: string) => {
        if (!isShippingRequiredForPayNow) return true;
        const hasValue = value != null && String(value).trim() !== "";
        return hasValue || "Please select a shipping method.";
      },
    }),
    [isShippingRequiredForPayNow]
  );

  useEffect(() => {
    if (!isShippingRequiredForPayNow) {
      clearErrors("requestedShippingMethod");
    }
  }, [isShippingRequiredForPayNow, clearErrors]);

  const handlePayLaterClick = () => {
    handleSubmit(
      () => onSubmit(false),
      (errors) => {
        if (sidebarPaymentMode !== "later" || !errors.requestedShippingMethod) return;
        clearErrors("requestedShippingMethod");
        const otherFieldErrors = Object.keys(errors).filter(
          (key) => key !== "requestedShippingMethod"
        );
        if (otherFieldErrors.length === 0) {
          onSubmit(false);
        }
      }
    )();
  };

  const buildOutsideOrderPayload = () => {
    const data = getValues();
    const practiceId = data.practiceDropdown || order?.orderDetails?.practiceId || PracticeSaveInfo?.practiceId;
    const practiceName = displayPracticeNameResolved;
    const editOrderId = Number(outsideOrderEditOrderId || order?.orderDetails?.orderId);

    const selectedProviderId = (data as any).providerDropdown;
    const providerDisplay = outsideManual.provider
      ? ((data.providerManual ?? "").trim())
      : providerOptions.find(p => String(p.value) === String(selectedProviderId))?.label ?? "";
    const shippingIsManual = outsideManual.shippingMethod || outsideManual.product;
    const shippingMethodDisplay = shippingIsManual
      ? (data as any).shippingMethodManual
      : shippingMethodOptions.find(o => o.value === data.requestedShippingMethod)?.label ?? "";
    const shippingAddressDisplay = outsideManual.shippingAddress
      ? (data as any).shippingAddressManual
      : locationOptions.find(o => String(o.value) === String(data.facilityAddress))?.label ?? "";
    const manualProductDisplay = String(watch("productManual" as any) ?? "").trim();
    const platformProductDisplay =
      selectedProduct?.label ??
      selectedPracticeProductInfo?.productName ??
      "";
    const productDisplay = outsideManual.product ? manualProductDisplay : platformProductDisplay;
    const patientDisplay = outsideManual.patient
      ? watch("patientManual" as any)
      : outsidePatientOptions.find(o => String(o.value) === String(watch("patientDropdown" as any)))?.label ?? "";

    // const op = parseFloat((data as any).outsideOrderPrice) || 0;
    // const sp = parseFloat((data as any).outsideShipPrice) || 0;
    // Resolve order price: prefer manually entered value, fall back to computed items total
    const manualOrderPrice = parseFloat((data as any).outsideOrderPrice);

    // For system product: compute total from size/qty rows using known unit prices
    const systemProductTotal = !outsideManual.product
      ? sizeQtyRows.reduce((s, r) => s + r.quantity * (getRowUnitPrice(r.productItemId) || 0), 0)
      : 0;

    // For manual product: compute from free-text items
    const manualProductTotal = outsideManual.product
      ? outsideProductItems.reduce((s, it) => s + it.qty * parseCurrencyInput(it.price), 0)
      : 0;

    const itemsTotal = outsideManual.product ? manualProductTotal : systemProductTotal;

    const op = !isNaN(manualOrderPrice) && manualOrderPrice > 0
      ? manualOrderPrice
      : itemsTotal;

    // Resolve shipping price: prefer manually entered value, fall back to resolved shipping cost
    const manualShipPrice = parseFloat((data as any).outsideShipPrice);
    const sp = !isNaN(manualShipPrice) && manualShipPrice > 0
      ? manualShipPrice
      : (resolvedShippingCost ?? 0);

    const toNullableNumber = (value: unknown) =>
      value === null || value === undefined || value === "" ? null : Number(value);

    // For system product: include productId and productItems from sizeQtyRows
    const systemProductId = !outsideManual.product
      ? selectedProduct?.value ?? selectedPracticeProductInfo?.productId ?? null
      : null;
    const systemPracticeProductId = !outsideManual.product
      ? selectedProduct?.practiceProductId ?? selectedPracticeProductInfo?.practiceProductId ?? null
      : null;
    const systemProductItems: OutsideOrderPayloadItem[] = !outsideManual.product
      ? sizeQtyRows
          .filter(r => r.productItemId != null && r.quantity > 0)
          .map(r => {
            const itemInfo = productIVR?.find(p => p.productItemId === r.productItemId);
            const rowInfo = listData.find(item => Number(item.id) === Number(r.productItemId));
            return {
              label: itemInfo?.size || rowInfo?.graftsNeeded || "",
              quantity: r.quantity,
              unitPrice: getRowUnitPrice(r.productItemId) || 0,
              productItemId: Number(r.productItemId),
            };
          })
      : [];

    const manualProductItems: OutsideOrderPayloadItem[] = outsideManual.product
      ? outsideProductItems
          .filter(it => it.label.trim())
          .map(it => ({
            label: it.label.trim(),
            quantity: it.qty,
            unitPrice: parseCurrencyInput(it.price),
            productItemId: null,
          }))
      : [];

    const resolvedPracticeProductId = toNullableNumber(systemPracticeProductId);
    const resolvedProductId = toNullableNumber(systemProductId);
    const productItemsForPayload = outsideManual.product ? manualProductItems : systemProductItems;

    const basePayload = {
      ...(isOutsideOrderEdit && editOrderId ? { orderId: editOrderId } : {}),
      manualPatient: outsideManual.patient ? patientDisplay : null,
      manualPractice: outsideManual.practice ? (data as any).practiceManual : null,
      manualProduct: outsideManual.product ? productDisplay : null,
      manualShippingAddress: outsideManual.shippingAddress ? shippingAddressDisplay : null,
      manualShippingMethod: shippingIsManual ? shippingMethodDisplay : null,
      manualProvider: outsideManual.provider ? providerDisplay : null,
      notes: (data as any).outsideNotes ?? "",
      dateOfService: data.dateOfService,
      orderDate: data.orderDate || dayjs().format("MM/DD/YYYY"),
      orderPrice: op,
      patient: patientDisplay,
      patientId: outsideManual.patient ? null : watch("patientDropdown" as any) ? Number(watch("patientDropdown" as any)) : null,
      paymentMethod: "later" as "now" | "later",
      practice: practiceName ?? "",
      practiceId: outsideManual.practice ? null : practiceId ?? null,
      provider: providerDisplay,
      providerId: outsideManual.provider ? null : selectedProviderId ? Number(selectedProviderId) : null,
      practiceProductId: resolvedPracticeProductId,
      product: productDisplay,
      productId: resolvedProductId,
      productItems: productItemsForPayload,
      shippingAddress: shippingAddressDisplay,
      shippingAddressId: outsideManual.shippingAddress ? null : data.facilityAddress ?? null,
      shippingMethod: shippingMethodDisplay,
      shippingMethodId: shippingIsManual ? null : data.requestedShippingMethod ?? null,
      shippingPrice: sp,
      total: op + sp,
    };

    if (!isOutsideOrderEdit) {
      return basePayload;
    }

    const changeMeta = computeOutsideOrderEditChangeMeta(outsideOrderEditBaseline.current, {
      isManualProduct: outsideManual.product,
      practiceProductId: resolvedPracticeProductId,
      productId: resolvedProductId,
      manualProductName: manualProductDisplay,
      platformItems: systemProductItems,
      manualItems: manualProductItems,
    });

    const preservePlaceholderProductItems =
      outsideManual.product &&
      !changeMeta.productChanged &&
      !changeMeta.itemsChanged &&
      outsideOrderEditBaseline.current?.hasPlaceholderItemsOnly;

    return {
      ...basePayload,
      productChanged: changeMeta.productChanged,
      itemsChanged: changeMeta.itemsChanged,
      ...(changeMeta.inactivateOrderItemIds.length
        ? { inactivateOrderItemIds: changeMeta.inactivateOrderItemIds }
        : {}),
      productItems: preservePlaceholderProductItems ? null : changeMeta.productItems,
    };
  };

  const saveOutsideOrder = async () => {
    const data = getValues();
    const op = parseFloat((data as any).outsideOrderPrice) || 0;
    const hasProvider = outsideManual.provider
      ? Boolean((data.providerManual ?? "").trim())
      : Boolean((data as any).providerDropdown);

    if (outsideManual.product && !op) {
      dispatch(setSingleAlertObj({ message: "Please enter the order price.", alertType: "error" }));
      return;
    }
    if (!hasProvider) {
      dispatch(setSingleAlertObj({ message: "Please select or enter a provider.", alertType: "error" }));
      return;
    }

    if (requireDateOfService && !isValidOrderDateValue(data.dateOfService)) {
      dispatch(setSingleAlertObj({ message: "Date of service is required.", alertType: "error" }));
      return;
    }

    try {
      setIsSavingOutside(true);
      const payload = buildOutsideOrderPayload();
      console.log(payload)
      const response = isOutsideOrderEdit
        ? await updateOutsideOrder(payload)
        : await createOutsideOrder(payload);
      const orderNumber = response?.data?.data?.orderNumber;
      dispatch(setSingleAlertObj({
        message: orderNumber
          ? `Order ${orderNumber} ${isOutsideOrderEdit ? "updated" : "saved"} successfully.`
          : `Order ${isOutsideOrderEdit ? "updated" : "saved"} successfully.`,
        alertType: "success"
      }));
      navigate("/order_list");
    } catch {
      dispatch(setSingleAlertObj({ message: "Unable to save order. Please try again.", alertType: "error" }));
    } finally {
      setIsSavingOutside(false);
    }
  };

  const outsideOrderOptionalFields: Array<keyof OrderFormState> = [
    "dateOfService",
    "facilityAddress",
    "shippingAddressManual",
  ];

  const handleSaveOutsideOrder = handleSubmit(
    () => saveOutsideOrder(),
    (formErrors) => {
      outsideOrderOptionalFields.forEach((field) => clearErrors(field));

      const blockingErrors = Object.keys(formErrors).filter(
        (key) => !outsideOrderOptionalFields.includes(key as keyof OrderFormState)
      );

      if (blockingErrors.length === 0) {
        saveOutsideOrder();
      }
    }
  );

  const usedSizeProductItemIds = useMemo(
    () =>
      new Set(
        sizeQtyRows
          .filter((r) => r.productItemId != null)
          .map((r) => Number(r.productItemId))
      ),
    [sizeQtyRows]
  );

  const canAddMoreSizes = useMemo(() => {
    if (!listData.length) return false;
    // Only allow adding another row if all current rows have a size selected.
    if (sizeQtyRows.some((r) => r.productItemId == null)) return false;
    return listData.some((d) => !usedSizeProductItemIds.has(Number(d.id)));
  }, [listData, usedSizeProductItemIds, sizeQtyRows]);

  useEffect(() => {
    if (outsideOrderEditUserModified.current || shippingMethodHydratedRef.current) return;
    if (outsideManual.shippingMethod || outsideManual.product) return;
    if (!order?.orderDetails?.orderId) return;
    if (!shippingMethodOptions.length) return;

    const info = order.orderDetails.orderInfo ?? {};
    const currentValue = getValues("requestedShippingMethod");
    if (currentValue && shippingMethodOptions.some(opt => String(opt.value) === String(currentValue))) {
      shippingMethodHydratedRef.current = true;
      return;
    }

    if (
      info.shippingMethodEntryType === "PLATFORM" &&
      info.shippingMethodId != null &&
      info.shippingMethodId !== ""
    ) {
      const matchedById = shippingMethodOptions.find(opt => String(opt.value) === String(info.shippingMethodId));
      if (matchedById) {
        setValue("requestedShippingMethod", matchedById.value);
        shippingMethodHydratedRef.current = true;
        return;
      }
    }

    if (!order.orderDetails.requestedShippingMethod) return;

    const savedLabel = order.orderDetails.requestedShippingMethod;
    const matched = shippingMethodOptions.find(opt => opt.label === savedLabel || String(opt.value) === String(savedLabel));
    if (matched) {
      setValue("requestedShippingMethod", matched.value);
      shippingMethodHydratedRef.current = true;
    }
  }, [order, shippingMethodOptions, setValue, getValues, outsideManual.shippingMethod, outsideManual.product])

  useEffect(() => {
    if (!isPastOrder || outsideManual.shippingMethod || outsideManual.product) return;
    const currentValue = getValues("requestedShippingMethod");
    if (!currentValue) return;
    if (
      !shippingMethodOptions.length ||
      !shippingMethodOptions.some((opt) => String(opt.value) === String(currentValue))
    ) {
      clearPlatformShippingMethod();
    }
  }, [isPastOrder, outsideManual.shippingMethod, outsideManual.product, shippingMethodOptions, getValues]);

  useEffect(() => {
    if (!isPastOrder || !outsideManual.product || outsideManual.shippingMethod) return;
    clearPlatformShippingMethod();
  }, [isPastOrder, outsideManual.product, outsideManual.shippingMethod]);

  // useEffect(() => {
  //   setValue("requestedShippingMethod", "Standard Overnight - (By 5 PM)");
  // }, [setValue]);

  const getCreateOrderPayload = (upfrontPayment = false): AddOrderInfo | null => {
    const data = getValues();
    const orderInfo = order?.orderDetails?.orderInfo ?? {};
    const resolvedProviderId = data.provider ?? orderInfo.providerId ?? order?.orderDetails?.providerId;
    const selectedProvider =
      providerOptions.find(provider => String(provider.value) === String(resolvedProviderId)) ??
      (order?.orderDetails?.providerName
        ? { label: order.orderDetails.providerName, value: resolvedProviderId }
        : undefined);
    const shippingMethodLabel = shippingMethodOptions.find(opt => opt.value === data.requestedShippingMethod)?.label ?? data.requestedShippingMethod;
    const shippingConfigId = data.requestedShippingMethod ? Number(data.requestedShippingMethod) : null;
    let createOrderObj: AddOrderInfo | null = null;

    const isReorderPayload = location?.state?.isReorder === true;
    const normalizedOrderDetailsType = order?.orderDetails?.orderType;
    const isRawDirectOrder = normalizedOrderDetailsType === "DIRECT_ORDER";
    const resolvedPracticeProductId =
      selectedProduct?.practiceProductId ??
      validPP?.practiceProductId ??
      orderInfo.practiceProductId ??
      order?.orderDetails?.practiceProductId ??
      order?.orderDetails?.orderItems?.[0]?.practiceProductId;

    if (
      order?.orderDetails !== undefined &&
      order?.orderType === ORDERTYPE.DIRECT_ORDER &&
      (order?.orderDetails.orderType === undefined || (isReorderPayload && isRawDirectOrder) || isReorderPayload)
    ) {
      createOrderObj = {
        practiceId: order.orderDetails.practiceId,
        ivrId: order.orderDetails.ivrId,
        productId: selectedProduct?.value ?? order.orderDetails.productId,
        practiceProductId: resolvedPracticeProductId,
        orderType: "DIRECT_ORDER",
        patientId: order.orderDetails.patientId,
        requestedDeliveryDate: data.requestedDeliveryDate,
        requestedShippingMethod: shippingMethodLabel,
        requestedShippingConfigId: shippingConfigId,
        dateOfService: data.dateOfService,
        poNumber: data.po,
        orderInfo: {
          practice: order?.orderDetails?.practiceName,
          patient: (order.orderDetails.patientFirstName || order.orderDetails.patientMiddleName || order.orderDetails.patientLastName) ?
            fullName(null, order.orderDetails.patientFirstName, order.orderDetails.patientMiddleName, order.orderDetails.patientLastName) : order.orderDetails.patientName,
          provider: selectedProvider?.label,
          facilityAddress: (data.facilityAddress.toString()),
          providerId: selectedProvider?.value
        },
        orderItems: graftsQtyList,
        orderId: order?.orderDetails?.orderId
      };
    } else if (order?.orderDetails === undefined && order?.orderType !== ORDERTYPE.DIRECT_ORDER && signedInPractice) {
      const patientId = outsideManual.patient
        ? undefined
        : (data as any).patientDropdown
          ? Number((data as any).patientDropdown)
          : getValues("patientId") ?? undefined;

      createOrderObj = {
        practiceId: signedInPractice.practiceId,
        productId: selectedProduct?.value,
        practiceProductId: selectedProduct?.practiceProductId,
        orderType: order?.orderType === ORDERTYPE.CONSIGNMENT_ORDER ? "CONSIGNMENT_ORDER" : order?.orderType === ORDERTYPE.PAST_ORDER ? "PASS_ORDER" : "DIRECT_ORDER",
        requestedDeliveryDate: data.requestedDeliveryDate,
        requestedShippingMethod: shippingMethodLabel,
        requestedShippingConfigId: shippingConfigId,
        dateOfService: data.dateOfService,
        poNumber: data.po,
        orderInfo: {
          practice: signedInPractice.practiceName,
          provider: selectedProvider?.label,
          facilityAddress: (selectedLocation),
          patient: resolvedPatientForPayload,
          providerId: selectedProvider?.value,
        },
        orderItems: graftsQtyList,
        orderDate: data.orderDate || order?.orderDate,
        patientId: patientId,
        manufacturerEmailEnable: manufacturerEmailNotifications
      };
    } else if (order?.orderDetails !== undefined && (order?.orderDetails.orderType === ORDERTYPE.DIRECT_ORDER || order?.orderDetails.orderType === "DIRECT_ORDER") && order.orderDetails.ivrId != null) {
      createOrderObj = {
        practiceId: order.orderDetails.practiceId,
        ivrId: order.orderDetails.ivrId,
        productId: selectedProduct?.value ?? order.orderDetails.productId,
        practiceProductId: selectedProduct?.practiceProductId ?? validPP?.practiceProductId,
        orderType: "DIRECT_ORDER",
        patientId: order.orderDetails.patientId,
        requestedDeliveryDate: data.requestedDeliveryDate,
        requestedShippingMethod: shippingMethodLabel,
        requestedShippingConfigId: shippingConfigId,
        dateOfService: data.dateOfService,
        poNumber: data.po,
        orderInfo: {
          practice: order?.orderDetails?.practiceName,
          patient: (order.orderDetails.patientFirstName || order.orderDetails.patientMiddleName || order.orderDetails.patientLastName) ?
            fullName(null, order.orderDetails.patientFirstName, order.orderDetails.patientMiddleName, order.orderDetails.patientLastName) : order.orderDetails.patientName,
          provider: selectedProvider?.label,
          facilityAddress: (data.facilityAddress.toString()),
          providerId: selectedProvider?.value
        },
        orderItems: graftsQtyList,
        orderId: order?.orderDetails?.orderId
      };
    } else if (order?.orderDetails !== undefined && order.orderDetails.ivrId == null && signedInPractice && (order?.orderDetails.orderType === ORDERTYPE.DIRECT_ORDER ||
      order?.orderDetails.orderType === ORDERTYPE.CONSIGNMENT_ORDER)) {
      createOrderObj = {
        practiceId: order.orderDetails.practiceId,
        ivrId: order.orderDetails.ivrId,
        productId: selectedProduct?.value ?? order.orderDetails.productId,
        practiceProductId: selectedProduct?.practiceProductId ?? validPP?.practiceProductId,
        orderType: order?.orderDetails.orderType === ORDERTYPE.CONSIGNMENT_ORDER ? "CONSIGNMENT_ORDER" : "DIRECT_ORDER",
        patientId: order.orderDetails.patientId,
        requestedDeliveryDate: data.requestedDeliveryDate,
        requestedShippingMethod: shippingMethodLabel,
        requestedShippingConfigId: shippingConfigId,
        dateOfService: data.dateOfService,
        poNumber: data.po,
        orderInfo: {
          practice: order?.orderDetails?.practiceName,
          patient: (order.orderDetails.patientFirstName || order.orderDetails.patientMiddleName || order.orderDetails.patientLastName) ?
            fullName(null, order.orderDetails.patientFirstName, order.orderDetails.patientMiddleName, order.orderDetails.patientLastName) :
            (order.orderDetails.patientName ? order.orderDetails.patientName : order.orderDetails.orderInfo?.patient),
          provider: selectedProvider?.label,
          facilityAddress: (data.facilityAddress.toString()),
          providerId: selectedProvider?.value
        },
        orderItems: graftsQtyList,
        orderId: order?.orderDetails?.orderId
      };
    }

    if (createOrderObj) {
      createOrderObj.upfrontPayment = upfrontPayment;
    }
    return createOrderObj;
  };

  const createOrder = async (upfrontPayment = false) => {
    const createOrderObj = getCreateOrderPayload(upfrontPayment);

    if (!createOrderObj) return;

    const response = await dispatch(addOrder(createOrderObj));
    if (response.meta.requestStatus === 'fulfilled') {
      const createdOrderId = resolveCreatedOrderId(response.payload);
      const orderNumber =
        typeof response.payload === "object" && response.payload !== null
          ? (response.payload as { orderNumber?: string }).orderNumber
          : createdOrderId != null
            ? String(createdOrderId)
            : undefined;

      if (upfrontPayment) {
        setUpfrontOrderResponse({
          orderId: createdOrderId,
          invoiceBalance: upfrontCardTotal,
          manufacturerStripeConfig: mfrStripeConfig || validPP?.manufacturerStripeConfig,
        });
        setShowStripePaymentTypePopup(true);
        return;
      }

      let successObj: SingleAlertInfo = {
        message: "Order " + orderNumber + " created successfully.",
        alertType: "success"
      };

      setShowOptionsPopup({ message: "", show: false });
      dispatch(setSingleAlertObj(successObj));
      navigate("/order_list");
    }
  };


  // CS-1845 START - Upfront payment checkout handler.
  // Step 1: addOrder is called with upfrontPayment=true to create the order.
  // Step 2: stripeUpfrontOrderCheckout is called with the new dedicated endpoint.
  const handleCheckout = async (_invoiceId: number, paymentMethodType: string, _paymentType: string, paymentAmount: number) => {
    // Guard against double-click / re-entry while already processing
    if (isProcessingStripePayment) return;

    let resolvedOrderId: number | null = null;
    let orderData: any = null;

    // CS-1845 - Set loading state to prevent UI interaction during payment processing
    setIsProcessingStripePayment(true);

    if (pendingUpfrontOrderPayload) {
      // CS-1845 - Step 1: Call addOrder to create the order.
      const payload = pendingUpfrontOrderPayload;
      const orderResponse = await dispatch(addOrder(payload));

      if (orderResponse.meta.requestStatus !== 'fulfilled') {
        // CS-1845 - Order creation failed; show error inside popup
        setIsProcessingStripePayment(false);
        setCheckoutError("Order creation failed. Please cancel and try again.");
        return;
      }

      orderData = orderResponse.payload;
      resolvedOrderId = resolveCreatedOrderId(orderData);

      setUpfrontOrderResponse({
        orderId: resolvedOrderId,
        invoiceBalance: upfrontCardTotal,
        manufacturerStripeConfig: mfrStripeConfig || validPP?.manufacturerStripeConfig,
        ...(typeof orderData === "object" && orderData !== null ? orderData : {}),
      });
      setPendingUpfrontOrderPayload(null);
    } else if (upfrontOrderResponse != null) {
      resolvedOrderId = resolveCreatedOrderId(upfrontOrderResponse);
    }

    if (!resolvedOrderId) {
      setIsProcessingStripePayment(false);
      setCheckoutError("Order ID could not be resolved. Please cancel and try again.");
      return;
    }

    // CS-1845 - Step 2: Call the dedicated upfront order checkout endpoint.
    const checkoutPayload = {
      orderId: resolvedOrderId,
      paymentMethod: paymentMethodType,
      successUrl: "/order_list",
      cancelUrl: "/order_list",
    };

    try {
      const response = await stripeUpfrontOrderCheckout(checkoutPayload);
      const resData = response?.data;

      setIsProcessingStripePayment(false);

      if (resData?.errors && resData.errors.length > 0) {
        // CS-1845 - API returned a business error
        const errorMessage = resData.errors[0].errorMessage || resData.errors[0].message;
        setCheckoutError(errorMessage || "Something went wrong. Please cancel and try again.");
      } else if (resData?.data?.checkoutUrl) {
        // CS-1845 - Redirect to Stripe checkout
        window.location.href = resData.data.checkoutUrl;
      } else if (resData?.data?.url) {
        window.location.href = resData.data.url;
      } else {
        setCheckoutError("Something went wrong. Please cancel and try again.");
      }
    } catch (error: any) {
      // CS-1845 - Network or unexpected error
      setIsProcessingStripePayment(false);
      const errorMessage =
        error?.data?.errors?.[0]?.errorMessage ||
        error?.data?.message ||
        error?.message ||
        "Something went wrong. Please cancel and try again.";
      setCheckoutError(errorMessage);
      console.error("CS-1845 - Upfront checkout error:", error);
    }
  };
  // CS-1845 END


  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const handleNavigation = () => {
    navigate(-1, { state: { withSearchHistory: true } })
  };

  // const signedInPractice: PracticeInfo | null = useMemo(() => {
  //   const selectedPracticeId = getValues("practiceDropdown");

  //   if (practiceByUserRole && (userInfo?.userRole === USER_ROLES.ROLE_PRACTICESTAFF)) {
  //     return practiceByUserRole[0];
  //   } else if (selectedPracticeId && canSeePracticeDropdown) {
  //     return PracticeSaveInfo || null;
  //   } else if (order?.orderDetails?.practiceId) {
  //     return PracticeSaveInfo || null;
  //   }

  //   return null;
  // }, [practiceByUserRole, getValues, order?.orderDetails?.practiceId, PracticeSaveInfo, canSeePracticeDropdown, userInfo?.userRole]);

  const signedInPractice: PracticeInfo | null = useMemo(() => {
    if (isPastOrder) {
      if (outsideManual.practice || !watchedPracticeDropdown) return null;
      return PracticeSaveInfo || null;
    }

    const selectedPracticeId = watchedPracticeDropdown;

    if ((selectedPracticeId && canSeePracticeDropdown) || order?.orderDetails?.practiceId) {
      return PracticeSaveInfo || null;
    }

    return null;
  }, [isPastOrder, outsideManual.practice, watchedPracticeDropdown, order?.orderDetails?.practiceId, PracticeSaveInfo, canSeePracticeDropdown]);

  useEffect(() => {
    if (isPastOrder) {
      if (outsideManual.practice || !watchedPracticeDropdown) {
        dispatch(resetPracticeInfoObject());
        return;
      }
      dispatch(fetchPracticeById(watchedPracticeDropdown));
      return;
    }

    const selectedPracticeId = watchedPracticeDropdown || order?.orderDetails?.practiceId;
    if (selectedPracticeId) {
      dispatch(fetchPracticeById(selectedPracticeId));
    }
  }, [watchedPracticeDropdown, outsideManual.practice, isPastOrder, dispatch, order?.orderDetails?.practiceId]);

  const providerOptions: Option[] = useMemo(() => {
    if (isPastOrder && (outsideManual.practice || !watchedPracticeDropdown)) {
      return [];
    }

    let options: Option[] = [];
    const today = dayjs();

    if (order?.orderType !== ORDERTYPE.DIRECT_ORDER) {
      signedInPractice?.practiceProviders?.map(provider => {

        if (provider.inactiveDate === null || (dayjs(provider.inactiveDate).isValid() && dayjs(provider.inactiveDate).isAfter(dayjs(today))) ||
          (dayjs(provider.inactiveDate).isValid() && dayjs(provider.inactiveDate).isSame(dayjs(today)))) {
          const providerName = fullName(provider.title ? provider.title : null, provider.firstName, provider.middleName, provider.lastName);
          options.push({
            label: providerName,
            value: provider.providerId
          });
        }
      })
    } else {
      const info = order?.orderDetails?.orderInfo ?? {};
      if (order?.orderDetails?.providerName || info.provider) {
        const providerIdFromOrderDetails =
          getReorderProviderId(order.orderDetails) ?? "";
        options.push({
          label: order.orderDetails.providerName ?? info.provider,
          value: providerIdFromOrderDetails
        });
        // setValue("provider", order.orderDetails.providerId);
      } else {
        signedInPractice?.practiceProviders?.map(provider => {

          if (provider.inactiveDate === null || (dayjs(provider.inactiveDate).isValid() && dayjs(provider.inactiveDate).isAfter(dayjs(today))) ||
            (dayjs(provider.inactiveDate).isValid() && dayjs(provider.inactiveDate).isSame(dayjs(today)))) {

            const providerName = fullName(provider.title ? provider.title : null, provider.firstName, provider.middleName, provider.lastName);
            options.push({
              label: providerName,
              value: provider.providerId
            });
          }
        })

        // if (order?.orderDetails?.orderInfo?.providerId) {
        //   setValue("provider", parseInt(order?.orderDetails?.orderInfo?.providerId));
        // }
      }
    };
    return options;
  }, [order, practiceByUserRole, signedInPractice, PracticeSaveInfo, isPastOrder, outsideManual.practice, watchedPracticeDropdown]);

  // Outside order - Fetch patients for selected practice
  const watchedPracticeForOutside = watch("practiceDropdown");
  useEffect(() => {
    if (!isPastOrder || outsideManual.practice || !watchedPracticeForOutside) {
      setOutsidePatientList([]);
      return;
    }
    dispatch(fetchPatientLookupByUserRole({ activePatientOnly: true, practiceId: Number(watchedPracticeForOutside) }))
      .then((response: any) => {
        if (response.meta.requestStatus === "fulfilled" && response.payload) {
          setOutsidePatientList(response.payload);
        }
      });
  }, [dispatch, isPastOrder, outsideManual.practice, watchedPracticeForOutside]);

  const outsidePatientOptions: Option[] = useMemo(() => {
    if (!isPastOrder) return [];
    const list = !outsideManual.practice && watchedPracticeForOutside ? outsidePatientList : patientsByUserRole;
    return (list ?? []).map((patient: any) => {
      const patientName = patient.patientName ||
        fullName(patient.title ?? null, patient.firstName, patient.middleName, patient.lastName);
      const dob = patient.patientDOB || patient.dateOfBirth || patient.birthDate;
      return {
        value: patient.patientId,
        label: dob ? `${patientName} (${dob})` : patientName,
      };
    });
  }, [isPastOrder, outsideManual.practice, watchedPracticeForOutside, outsidePatientList, patientsByUserRole]);

  // Resolve patient display name for outside/past order flows
  const resolvedPatientForPayload = (() => {
    if (isPastOrder) {
      if (outsideManual.patient) {
        return (getValues as any)("patientManual") || "";
      }
      const patientId = (getValues as any)("patientDropdown");
      return outsidePatientOptions.find(
        (o) => String(o.value) === String(patientId)
      )?.label ?? "";
    }
    return fullName(
      null,
      getValues("patientInfo.patientFirstName"),
      getValues("patientInfo.patientMiddleName"),
      getValues("patientInfo.patientLastName")
    );
  })();

  useEffect(() => {
    if (isPastOrder) {
      if (outsideManual.practice || !watchedPracticeDropdown || outsideOrderEditUserModified.current) return;
      if (providerOptions.length === 0) return;

      const info = order?.orderDetails?.orderInfo ?? {};
      const providerIdToSet =
        order?.orderDetails?.providerId ??
        order?.orderDetails?.practiceProviderId ??
        getReorderProviderId(order?.orderDetails);
      if (providerIdToSet === null || providerIdToSet === undefined || providerIdToSet === "") return;

      const matchedOption = providerOptions.find(opt => String(opt.value) === String(providerIdToSet));
      if (!matchedOption) return;

      const currentProvider = getValues("providerDropdown" as any);
      if (isReorder) {
        if (currentProvider === matchedOption.value) return;
      } else if (currentProvider !== undefined && currentProvider !== null && currentProvider !== "") {
        return;
      }

      setValue("providerDropdown" as any, matchedOption.value);
      return;
    }

    if (providerOptions.length === 0) return;

    const info = order?.orderDetails?.orderInfo ?? {};
    const providerIdToSet =
      location?.state?.providerId ??
      location?.state?.practiceProviderId ??
      order?.orderDetails?.providerId ??
      order?.orderDetails?.practiceProviderId ??
      getReorderProviderId(order?.orderDetails);
    if (providerIdToSet === null || providerIdToSet === undefined || providerIdToSet === "") return;

    const matchedOption = providerOptions.find(opt => String(opt.value) === String(providerIdToSet));
    if (!matchedOption) return;

    const currentProvider = getValues("provider");
    if (isReorder) {
      if (currentProvider === matchedOption.value) return;
    } else if (currentProvider !== undefined && currentProvider !== null && currentProvider !== "") {
      return;
    }

    setValue("provider", matchedOption.value);
  }, [providerOptions, location?.state?.providerId, location?.state?.practiceProviderId, order?.orderDetails, getValues, setValue, isPastOrder, isReorder, outsideManual.practice, watchedPracticeDropdown]);

  const practiceOptions: Option[] = useMemo(() => {
    if (practiceByUserRole) {
      const sortedPracticeInfo: PracticeInfo[] = sortNames(practiceByUserRole, "practiceName");
      return sortedPracticeInfo.map((practice: PracticeInfo) => {
        return {
          value: practice.practiceId,
          label: practice.practiceName
        }
      });
    }
    return [];
  }, [practiceByUserRole])

  const providerSummarySubtitle = useMemo(() => {
    const pid = watchedProviderId;
    if (pid === null || pid === undefined || pid === "") return null;
    return providerOptions.find((o) => String(o.value) === String(pid))?.label ?? null;
  }, [watchedProviderId, providerOptions]);

  const formatLocationName = (practiceLocation: PracticeLocationInfo) => {
    const locationParts = [practiceLocation.streetAddress, practiceLocation.unitNumber, practiceLocation.city, practiceLocation.state, practiceLocation.zipCode];
    const filteredLocationParts = locationParts.filter(part => part !== undefined && part !== null && part.trim() !== "");
    const locationName = filteredLocationParts?.join(", ")
    return locationName;
  }

  const locationOptions: Option[] = useMemo(() => {
    let options: Option[] = [];
    if (order?.orderType !== ORDERTYPE.DIRECT_ORDER) {
      if (order?.orderType === ORDERTYPE.PAST_ORDER) {
        if (!outsideManual.practice && watchedPracticeDropdown && activePracticeLocations?.length) {
          activePracticeLocations.forEach((practiceLocation) => {
            const locationName = formatLocationName(practiceLocation);
            options.push({
              label: locationName,
              value: practiceLocation.locationId
            });
          });
        }
      } else {
        signedInPractice?.practiceLocations
          ?.filter(practiceLocation => practiceLocation.inactiveDate === null)
          .forEach(practiceLocation => {
            const locationName = formatLocationName(practiceLocation);
            options.push({
              label: locationName,
              value: practiceLocation.locationId,
            });
          });
      }
    } else {
      if (PracticeSaveInfo) {
        PracticeSaveInfo.practiceLocations?.
          filter(practiceLocation => {
            return (
              practiceLocation.inactiveDate == null ||
              dayjs(practiceLocation.inactiveDate).isAfter(dayjs(), "day")
            );
          }).
          map(practiceLocation => {
            const locationName = formatLocationName(practiceLocation);
            options.push({
              label: locationName,
              value: practiceLocation.locationId
            });
          });
        if (order?.orderDetails?.locationId) {
          setValue("facilityAddress", Number(order.orderDetails.locationId));
        } else {
          const info = order?.orderDetails?.orderInfo ?? {};
          if (
            info.shippingAddressEntryType === "PLATFORM" &&
            info.shippingAddressId != null &&
            info.shippingAddressId !== ""
          ) {
            setValue("facilityAddress", Number(info.shippingAddressId));
            setSelectedLocation(String(info.shippingAddressId));
          } else if (info.facilityAddress) {
            setValue("facilityAddress", parseInt(info.facilityAddress));
          }
        }
      }
    };
    return options;
  }, [order, practiceByUserRole, locationDetails, signedInPractice, PracticeSaveInfo, activePracticeLocations, isPastOrder, outsideManual.practice, watchedPracticeDropdown]);

  useEffect(() => {
    if (!isReorder || !order?.orderDetails?.orderInfo) return;

    const info = order.orderDetails.orderInfo;
    if (info.shippingAddressEntryType !== "PLATFORM") return;

    const shippingAddressId = info.shippingAddressId ?? order.orderDetails.locationId;
    if (shippingAddressId == null || shippingAddressId === "") return;

    const matchedOption = locationOptions.find((option) => String(option.value) === String(shippingAddressId));
    if (!matchedOption) return;

    const currentValue = getValues("facilityAddress");
    if (currentValue === matchedOption.value) return;

    setValue("facilityAddress", matchedOption.value);
    setSelectedLocation(String(matchedOption.value));
  }, [isReorder, order?.orderDetails, locationOptions, getValues, setValue]);

  useEffect(() => {
    if (!isReorder || !order?.orderDetails?.orderInfo) return;

    const info = order.orderDetails.orderInfo;
    if (info.providerEntryType === "MANUAL") return;

    const providerId = getReorderProviderId(order.orderDetails);
    if (providerId == null || providerId === "") return;
    if (providerOptions.length === 0) return;
    const matchedOption = providerOptions.find((option) => String(option.value) === String(providerId));
    if (!matchedOption) return;

    const providerField = isPastOrder ? "providerDropdown" : "provider";
    const currentProvider = getValues(providerField as any);
    if (currentProvider === matchedOption.value) return;

    setValue(providerField as any, matchedOption.value);
  }, [isReorder, isPastOrder, order?.orderDetails, providerOptions, getValues, setValue]);

  const handleChange = (_name: string, value: string) => {
    if (!outsideManual.shippingMethod) {
      clearPlatformShippingMethod();
    }

    if (isOutsideOrderEdit) {
      resetOutsideEditPlatformOrderItemPrices();
    }

    if (!value) {
      if (!outsideManual.product) {
        clearPlatformProductAndSizes(!outsideManual.shippingMethod);
      }
      return;
    }

    const selectProduct = practiceProductsList.find((prod: any) => String(prod.practiceProductId) === String(value));
    if (!selectProduct) return;

    setGraftsQtyList([]);
    setSizeQtyRows([]);
    sizeQtyPrevKeysRef.current = new Set();
    setProductIVR(undefined);
    setListData([]);
    setValue("graftQty", []);

    setSelectedProduct({
      label: selectProduct.productName,
      value: selectProduct.productId,
      practiceProductId: selectProduct.practiceProductId
    });
    setValue("product", value);

    const providerField = isPastOrder ? "providerDropdown" : "provider";
    if (getValues(providerField as any) == null || getValues(providerField as any) === "") {
      setValue(providerField as any, selectProduct?.practiceProviderId ? selectProduct.practiceProviderId : "");
    }
  };

  const productOptions = useMemo(() => {
    if (isPastOrder && (outsideManual.practice || !watchedPracticeDropdown)) {
      return [];
    }

    if (isPastOrder && (outsideManual.provider || !watchedProviderId)) {
      return [];
    }

    let options: { label: string; value: number; productId: number; macId: number | null; macName: string | null }[] = [];
    practiceProductsList.map((option: any) => {
      const macName = option.macId && option.macName ? " - " + option.macName : '';
      const providerName = option.billingType === "INDIVIDUAL" && option.providerName ? ` (${option.providerName}) ` : ""
      options.push({
        label: `${option.productName}${providerName}${macName}`,
        value: option.practiceProductId,
        productId: option.productId,
        macId: option.macId ? option.macId : null,
        macName: option.macName ? option.macName : null
      });
    });
    return options;
  }, [practiceProductsList, isPastOrder, outsideManual.practice, outsideManual.provider, watchedPracticeDropdown, watchedProviderId]);

  useEffect(() => {
    if (!isReorder || outsideOrderEditUserModified.current) return;
    if (!productOptions.length || !practiceProductsList.length) return;

    const practiceProductId =
      order?.orderDetails?.practiceProductId ??
      order?.orderDetails?.orderInfo?.practiceProductId ??
      order?.orderDetails?.orderItems?.[0]?.practiceProductId;
    if (practiceProductId == null || practiceProductId === "") return;

    const matchedOption = productOptions.find((option) => String(option.value) === String(practiceProductId));
    if (!matchedOption) return;

    const currentValue = getValues("product");
    if (currentValue !== matchedOption.value) {
      setValue("product", matchedOption.value);
    }

    const currentProduct = practiceProductsList.find((prod: any) =>
      sameValue(prod.practiceProductId, matchedOption.value)
    );
    if (currentProduct && !selectedProduct) {
      setSelectedProduct({
        label: currentProduct.productName,
        value: currentProduct.productId,
        practiceProductId: currentProduct.practiceProductId,
      });
    }
  }, [
    isReorder,
    productOptions,
    practiceProductsList,
    order?.orderDetails,
    getValues,
    setValue,
    selectedProduct,
  ]);

  const displayPracticeName = () => {
    if (order?.orderType !== ORDERTYPE.DIRECT_ORDER) {
      return signedInPractice?.practiceName;
    } else {
      return order?.orderDetails?.practiceName;
    };
  };

  const displayPatientName = () => {
    if (order?.orderType === ORDERTYPE.CONSIGNMENT_ORDER) {
      return null;
    } else if (order?.orderType === ORDERTYPE.DIRECT_ORDER) {
      if (order?.orderDetails) {
        if (order?.orderDetails?.patientName) {
          return order.orderDetails.patientName;
        }
        if (order.orderDetails.orderInfo?.patient) {
          return order.orderDetails.orderInfo?.patient;
        }
        return fullName(null, order.orderDetails.patientFirstName, order.orderDetails.patientMiddleName, order.orderDetails.patientLastName);
      }
    };
  };

  const handleDateChangeService = async (date: Date | null) => {
    if (date == null) {
      setValue("dateOfService", "", { shouldValidate: true });
      return;
    }
    setValue("dateOfService", dayjs(date).format("MM/DD/YYYY"), { shouldValidate: true });
  };

  const handleDateChangeOrder = (date: Date | null) => {
    setValue("requestedDeliveryDate", dayjs(date).format("MM/DD/YYYY"));
  };

  const handleOrderDateChange = (date: Date | null) => {
    if (!date) {
      setValue("orderDate", "");
      setOrder((prevOrder) => ({
        ...prevOrder,
        orderDate: "",
      }));
      return;
    }

    const selectedDate = dayjs(date);
    const formattedDate = selectedDate.format("MM/DD/YYYY");
    setValue("orderDate", formattedDate);
    setOrder((prevOrder) => ({
      ...prevOrder,
      orderDate: formattedDate,
    }));

    if (selectedDate.isAfter(dayjs(), "day")) {
      setShowFutureOrderDateWarning(true);
    }
  };

  const setRowQuantity = (productItemId: number, quantity: number) => {
    const normalizedQty = Math.max(0, Number(quantity) || 0);
    setValue(`graftQty.${productItemId}` as any, normalizedQty === 0 ? "" : String(normalizedQty));
    setGraftsQtyList((prevValues) => {
      const updated = (prevValues ? [...prevValues] : []).filter((p) => p.productItemId !== productItemId);
      if (normalizedQty > 0) updated.push({ productItemId, quantity: normalizedQty });
      return updated;
    });
  };

  // Hydrate Sizes & Quantity from graftsQtyList when rows are still empty, or only placeholder rows exist
  // (user clicked Add Size before order lines loaded — reorder lines must still replace placeholders).
  useEffect(() => {
    if (isOutsideOrderEdit && outsideOrderEditUserModified.current) return;
    if (!graftsQtyList || graftsQtyList.length === 0) {
      if (sizeQtyRows.length > 0) setSizeQtyRows([]);
      return;
    }
    const placeholderOnly =
      sizeQtyRows.length > 0 &&
      sizeQtyRows.every((r) => r.productItemId == null);
    if (sizeQtyRows.length > 0 && !placeholderOnly) return;
    setSizeQtyRows(
      graftsQtyList.map((g) => ({
        key: `${g.productItemId}`,
        productItemId: g.productItemId,
        quantity: Math.max(0, Number(g.quantity) || 0),
      }))
    );
  }, [graftsQtyList, isOutsideOrderEdit]);

  useEffect(() => {
    if (!outsideManual.product) {
      lastOutsideItemsOrderPriceRef.current = null;
      return;
    }
    const total = outsideProductItems.reduce((s, it) => s + it.qty * parseCurrencyInput(it.price), 0);
    if (total > 0) {
      const nextOrderPrice = total.toFixed(2);
      lastOutsideItemsOrderPriceRef.current = nextOrderPrice;
      setValue("outsideOrderPrice" as any, nextOrderPrice);
      return;
    }

    const currentOrderPrice = String(getValues("outsideOrderPrice" as any) ?? "");
    if (
      lastOutsideItemsOrderPriceRef.current !== null &&
      currentOrderPrice === lastOutsideItemsOrderPriceRef.current
    ) {
      setValue("outsideOrderPrice" as any, "");
    }
    lastOutsideItemsOrderPriceRef.current = null;
  }, [outsideManual.product, outsideProductItems, getValues, setValue]);

  // Keep react-hook-form `sizeQty.*` fields in sync with row state so CustomDropdown shows selection
  // after hydration, row order changes, or programmatic updates. Unregister removed row keys.
  useLayoutEffect(() => {
    const nextKeys = new Set(sizeQtyRows.map((r) => r.key));
    sizeQtyPrevKeysRef.current.forEach((key) => {
      if (!nextKeys.has(key)) {
        unregister(`sizeQty.${key}` as never);
      }
    });
    sizeQtyPrevKeysRef.current = nextKeys;

    sizeQtyRows.forEach((r) => {
      const path = `sizeQty.${r.key}`;
      const rawId = r.productItemId;
      let formValue: string | number | "" = "";
      if (rawId !== null && rawId !== undefined) {
        // Use the exact `id` from listData so CustomDropdown option.value === field.value (strict) holds.
        const match = listData.find((d) => String(d.id) === String(rawId));
        formValue = match ? match.id : Number(rawId);
      }
      setValue(path as never, formValue as never, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    });
  }, [sizeQtyRows, listData, setValue, unregister]);

  const handlePracticeChange = async (_?: any, value?: string | number | null) => {
    const selectedPracticeId =
      value !== undefined && value !== null ? value : getValues("practiceDropdown");
    outsideOrderEditUserModified.current = true;
    dispatch(updatePracticeProductsInfoObject([]));
    setMfrStripeConfig(null);
    setValidPP(null);

    if (!selectedPracticeId) {
      clearPracticeDependentFields(outsideManual);
      setIsDisableProducts(true);
      return;
    }

    if (isPastOrder) {
      clearPracticeDependentFields(outsideManual);
      setIsDisableProducts(false);
      dispatch(fetchPracticeProductsDataWithModule({
        practiceId: Number(selectedPracticeId),
        moduleName: "CREATE_ORDER"
      }));
      return;
    }

    dispatch(fetchPracticeProductsDataWithModule({
      practiceId: Number(selectedPracticeId),
      moduleName: "CREATE_ORDER"
    }));
    productData();
  };

  const handleProviderChange = async (_: any, value: string | number) => {
    const selectedProviderId = value;
    if (!isReorder) {
      outsideOrderEditUserModified.current = true;
    }
    setValidPP(null);

    if (selectedProviderId === null || selectedProviderId === undefined || selectedProviderId === "") {
      clearProviderDependentFields(outsideManual);
      dispatch(updatePracticeProductsInfoObject([]));
      return;
    }

    if (!isReorder && !outsideManual.shippingAddress) {
      setValue("facilityAddress", "");
      setSelectedLocation("");
    }

    if (!isReorder && !outsideManual.product) {
      clearPlatformProductAndSizes(!outsideManual.shippingMethod);
    }

    const selectedPracticeId = getValues("practiceDropdown");
    const practiceId = selectedPracticeId || PracticeSaveInfo?.practiceId;
    if (practiceId) {
      const response = await dispatch(fetchPracticeProductsDataUsingDateOfServiceAndProvider({
        practiceId: Number(practiceId),
        providerId: Number(selectedProviderId),
        moduleName: "CREATE_ORDER",
        dateOfService: order.orderDate ? order.orderDate : ""
      }));

      if (response?.meta?.requestStatus === "fulfilled") {
        const fetchedProducts = normalizePracticeProducts(response);
        if (!isOutsideOrderEdit || isReorder) {
          applyProductSelection(fetchedProducts);
        }
      }
    }

  };

  const handleLocationChange = (name: string, value: string) => {
    const selectedLocation = locationOptions?.find(practiceLocation => practiceLocation.value === value);
    if (selectedLocation) {
      setSelectedLocation(selectedLocation.value.toString());
    }
  };

  const fetchStreetSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) return [];
    try {
      return await dispatch(
        autoAddress({
          addressInput: query,
          interceptorOption: { skipErrorPopup: true },
        })
      ).unwrap();
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      return [];
    }
  }, [dispatch]);

  const transformAddressResponse = useCallback((response: any) => {
    return (response?.data || []).map((item: any) => ({
      value: item.description,
      data: item.placeId,
      label: item.description,
    }));
  }, []);

  const productData = () => {
    setIsDisableProducts(false);
    resetSelectedProductState();
    if (isPastOrder) {
      setValue("providerDropdown" as any, "");
    } else {
      setValue("provider", "");
    }
    setValue("facilityAddress", "");
    setListData([]);
  };

  const handlePatientDOB = (date: Date | null) => {
    setValue(`patientInfo.${"birthDate"}`, dayjs(date).format("MM/DD/YYYY"));
  }

  const patientGenderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  const emailToManufacturerOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const handleOrderDate = (date: Date | null) => {
    if (order?.orderType === ORDERTYPE.PAST_ORDER) {
      dispatch(fetchActiveLocationsForGivenDate({
        practiceId: String(location?.state?.practiceId),
        effectiveDate: dayjs(date).format("MM/DD/YYYY")
      }));
    }

    setOrder((prevOrder) => ({
      ...prevOrder,
      orderDate: date ? dayjs(date).format("MM/DD/YYYY") : "", // Convert Date to string format
    }));

    resetSelectedProductState();
    setValue("provider", "");
    setValue("facilityAddress", "");
    setSelectedLocation("");
  };

  const handleManufacturerEmail = (e: any) => {
    if (e.target.value === "true") {
      setManufacturerEmailNotifications(true);
    } else {
      setManufacturerEmailNotifications(false);
    }
  }

  const outsideSummaryItems = useMemo(() => {
    if (!outsideManual.product) return [];
    return outsideProductItems
      .filter((it) => it.label.trim() !== "")  // ← only show named items
      .map((it, idx) => ({
        productItemId: it.id,
        size: it.label,
        productItemDescription: null,
        units: 1,
      }));
  }, [outsideManual.product, outsideProductItems]);

  const outsideSkuPrices = useMemo(() => {
    if (!outsideManual.product || outsideProductItems.length === 0) return null;
    const namedItems = outsideProductItems.filter((it) => it.label.trim() !== "");
    if (namedItems.length === 0) return null;
    return namedItems.map((it) => ({
      productItemId: it.id,
      size: it.label,
      extendedPrice: parseCurrencyInput(it.price),
      upfrontPrice: null,
      upfrontDiscount: null,
    }));
  }, [outsideManual.product, outsideProductItems]);

  const outsideOrderQuantities = useMemo(() => {
    if (!outsideManual.product) return {};
    return outsideProductItems
      .filter((it) => it.label.trim() !== "")  // ← same filter
      .reduce((acc, it) => {
        acc[it.id] = it.qty;
        return acc;
      }, {} as { [id: number]: number });
  }, [outsideManual.product, outsideProductItems]);

  const displayPatientNameResolved = useMemo(() => {
    if (isPastOrder) {
      // Manual entry
      if (outsideManual.patient) return watch("patientManual" as any) || null;
      // System dropdown selection label
      const patientId = watch("patientDropdown" as any);
      return outsidePatientOptions.find(o => String(o.value) === String(patientId))?.label ?? null;
    }
    if (showPatientFromLocation) {
      return fullName("", getValues("patientInfo.patientFirstName"), getValues("patientInfo.patientMiddleName"), getValues("patientInfo.patientLastName")) || null;
    }
    if (showPatientFromOrder) return displayPatientName() || null;
    return null;
  }, [
    isPastOrder, outsideManual.patient,
    watch("patientManual" as any), watch("patientDropdown" as any),
    outsidePatientOptions, showPatientFromLocation, showPatientFromOrder
  ]);

  const displayPracticeNameResolved = useMemo(() => {
    if (isPastOrder && outsideManual.practice) return watch("practiceManual" as any) || null;
    return displayPracticeName() || null;
  }, [
    isPastOrder,
    outsideManual.practice,
    watch("practiceManual" as any),
    order?.orderDetails?.practiceName,
    signedInPractice?.practiceName,
  ]);

  return (
    <Box sx={{
      margin: {
        xs: "20px 10px",
        sm: "20px",
      },
    }}>
      {showOptionPopup.show &&
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      }
      <InfoPopup
        open={showFutureOrderDateWarning}
        content="The selected order date is in the future. Please verify the date before continuing."
        onClose={() => setShowFutureOrderDateWarning(false)}
        title="Future Order Date"
        severity="warning"
      />
      <Box mb={1.5} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} onClick={handleNavigation} />
        <Box>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>Orders</Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            {isOutsideOrderEdit ? `Edit Order  - ${order?.orderDetails?.orderNumber ?? ""}` : isOutsideRecordOrder ? "Record Order" : "Create Order"}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            {isOutsideOrderEdit
              ? "Update the outside order details below. Platform and new-entry fields can be mixed as needed."
              : isOutsideRecordOrder
              ? `Log an order that was completed outside the platform. Mark any field as "Not in the Platform" to enter it manually.`
              : "Fill in the order details below"}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={(e) => { e.preventDefault(); }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 1.5, alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={cardSx}>
              <SectionHeader icon={<WidgetsOutlined />} title="Order References" action={<Typography sx={{ fontSize: "0.75rem", color: mTokens.textSecondary }}>Step 1 of 2</Typography>} />
              <Box sx={{ px: 2.8, pt: 1.8, pb: 2.2 }}>
                  <Grid container spacing={2.5}>
                  {isOutsideRecordOrder ? (
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Order Date" required />
                      <Box sx={datepickerWrapSx(!!errors.orderDate)}>
                        <CustomDatePicker
                          control={control}
                          onChange={handleOrderDateChange}
                          controllerName="orderDate"
                          label=""
                          size="small"
                          rules={{ required: "Order Date is required." }}
                        />
                      </Box>
                    </Grid>) : (<></>)}

                  <Grid item xs={12} md={6}>
                    <FieldLabel label="Practice" required={isOutsideRecordOrder || (order?.orderType !== ORDERTYPE.DIRECT_ORDER && isMultiPracticeUser)} />
                    {isOutsideRecordOrder ? (
                      <Box sx={dropdownWrapSx()}>
                        <CustomDropdownField
                          key={canEnterOutsideManual && outsideManual.practice ? "practice-manual" : "practice-platform"}
                          name={canEnterOutsideManual && outsideManual.practice ? "practiceManual" : "practiceDropdown"}
                          control={control}
                          options={practiceOptions}
                          label=""
                          size="small"
                          comboStyle={!(canEnterOutsideManual && outsideManual.practice)}
                          withoutSysData={canEnterOutsideManual && outsideManual.practice}
                          placeholderText={canEnterOutsideManual && outsideManual.practice ? "Enter practice name" : "Search practices…"}
                          addManuallyLabel={canEnterOutsideManual ? "New practice" : undefined}
                          onAddManually={canEnterOutsideManual ? (val) => goOutsideManual("practice", val) : undefined}
                          onUseSystem={canEnterOutsideManual && outsideManual.practice ? () => goOutsideSystem("practice") : undefined}
                          onChangeSelect={handlePracticeChange}
                          disabled={isReorder}
                          rules={{ required: "Please select or enter a practice." }}
                        />
                      </Box>
                    ) : canSeePracticeDropdown ? (
                      <Box sx={dropdownWrapSx()}>
                        <CustomDropdownField
                          name='practiceDropdown'
                          control={control}
                          onChangeSelect={handlePracticeChange}
                          options={practiceOptions}
                          label=""
                          size="small"
                          placeholderText="Search practices…"
                          rules={order?.orderType !== ORDERTYPE.DIRECT_ORDER && isMultiPracticeUser ? { required: "Practice is required." } : {}}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ px: 1.5, py: 1, border: `1px solid ${mTokens.inputBorder}`, borderRadius: "10px", backgroundColor: mTokens.inputBg, minHeight: 38, display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600 }}>{displayPracticeName()}</Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {isOutsideRecordOrder ? (
                      <>
                        <FieldLabel label="Provider" required />
                        <Box sx={dropdownWrapSx()}>
                          <CustomDropdownField
                            key={canEnterOutsideManual && outsideManual.provider ? "provider-manual" : "provider-platform"}
                            name={canEnterOutsideManual && outsideManual.provider ? "providerManual" : "providerDropdown"}
                            control={control}
                            options={providerOptions}
                            label=""
                            size="small"
                            comboStyle={!(canEnterOutsideManual && outsideManual.provider)}
                            withoutSysData={canEnterOutsideManual && outsideManual.provider}
                            disabled={isReorder || (!outsideManual.provider && !outsideManual.practice && !watchedPracticeDropdown)}
                            placeholderText={canEnterOutsideManual && outsideManual.provider ? "Enter provider name" : "Search providers…"}
                            addManuallyLabel={canEnterOutsideManual ? "New provider" : undefined}
                            onAddManually={canEnterOutsideManual ? (val) => goOutsideManual("provider", val) : undefined}
                            onUseSystem={canEnterOutsideManual && outsideManual.provider ? () => goOutsideSystem("provider") : undefined}
                            onChangeSelect={handleProviderChange}
                            rules={{ required: "Please select or enter a provider." }}
                          />
                        </Box>
                      </>
                    ) : (
                      <>
                        <FieldLabel label="Provider" required={order?.orderType !== ORDERTYPE.DIRECT_ORDER} />
                        <Box sx={dropdownWrapSx()}>
                          <CustomDropdownField
                            name="provider"
                            control={control}
                            options={providerOptions}
                            onChangeSelect={handleProviderChange}
                            label=""
                            size="small"
                            placeholderText="Search providers…"
                            disabled={location.state?.isReorder || ((location.state?.orderType === ORDERTYPE.ORDERED_FROM_PATIENT_LIST || location.state?.orderType === ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR) ? false : order?.orderType !== ORDERTYPE.DIRECT_ORDER && isDisableProducts)}
                            rules={order?.orderType !== ORDERTYPE.DIRECT_ORDER ? { required: "Provider is required." } : {}}
                          />
                        </Box>
                      </>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {isOutsideRecordOrder ? (
                      <>
                        <FieldLabel label="Patient" required />
                        <Box sx={dropdownWrapSx()}>
                          <CustomDropdownField
                            key={canEnterOutsideManual && outsideManual.patient ? "patient-manual" : "patient-platform"}
                            name={canEnterOutsideManual && outsideManual.patient ? "patientManual" : "patientDropdown"}
                            control={control}
                            options={outsidePatientOptions}
                            label=""
                            size="small"
                            comboStyle={!(canEnterOutsideManual && outsideManual.patient)}
                            withoutSysData={canEnterOutsideManual && outsideManual.patient}
                            placeholderText={canEnterOutsideManual && outsideManual.patient ? "Enter patient name" : "Search patients…"}
                            addManuallyLabel={canEnterOutsideManual ? "New patient" : undefined}
                            onAddManually={canEnterOutsideManual ? (val) => goOutsideManual("patient", val) : undefined}
                            onUseSystem={canEnterOutsideManual && outsideManual.patient ? () => goOutsideSystem("patient") : undefined}
                            rules={{ required: "Please select or enter a patient." }}
                          />
                        </Box>
                      </>
                    ) : (
                      <>
                      </>
                    )}
                  </Grid>

                    <Grid item xs={12}>
                      <FieldLabel label="Product" required={isProductRequiredOrderType} />
                      {isOutsideRecordOrder ? (
                        <Box sx={dropdownWrapSx()}>
                          <CustomDropdownField
                            key={canEnterOutsideManual && outsideManual.product ? "product-manual" : "product-platform"}
                            name={canEnterOutsideManual && outsideManual.product ? "productManual" : "product"}
                            control={control}
                            options={
                              canEnterOutsideManual && (outsideManual.product || outsideManual.provider)
                                ? []
                                : productOptions
                            }
                            onChangeSelect={canEnterOutsideManual && outsideManual.product ? undefined : handleChange}
                            label=""
                            size="small"
                            sortOptions={false}
                            comboStyle={!(canEnterOutsideManual && outsideManual.product)}
                            withoutSysData={canEnterOutsideManual && outsideManual.product}
                            disabled={
                              isReorder ||
                              (!outsideManual.product &&
                              !outsideManual.practice &&
                              !outsideManual.provider &&
                              (!watchedPracticeDropdown || !watchedProviderId))
                            }
                            placeholderText={canEnterOutsideManual && outsideManual.product ? "Enter product name" : "Search products…"}
                            addManuallyLabel={canEnterOutsideManual ? "New product" : undefined}
                            onAddManually={canEnterOutsideManual ? (val) => goOutsideManual("product", val) : undefined}
                            onUseSystem={canEnterOutsideManual && outsideManual.product ? () => goOutsideSystem("product") : undefined}
                            rules={{ required: "Please select or enter a product." }}
                          />
                        </Box>
                      ) : (
                        <Box sx={dropdownWrapSx()}>
                          <CustomDropdownField
                            name="product"
                            control={control}
                            onChangeSelect={handleChange}
                            options={productOptions}
                            label=""
                            size="small"
                            placeholderText="Search products…"
                            rules={isProductRequiredOrderType ? { required: "Product is required." } : {}}
                            disabled={(location.state?.orderType === ORDERTYPE.ORDERED_FROM_PATIENT_LIST || location.state?.orderType === ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR) ? false : isDisableProducts}
                          />
                        </Box>
                      )}

                    {/* Items / sizes — shown for system product OR manually entered product */}
                    {(outsideManual.product || (!outsideManual.product && selectedProduct)) && (
                      <Box sx={{ mt: 1, borderRadius: "10px", border: `1px dashed ${mTokens.inputBorder}`, p: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={(outsideManual.product ? outsideProductItems.length : sizeQtyRows.length) ? 1.2 : 0.5}>
                          <StraightenOutlined sx={{ fontSize: "0.8rem", color: "#c4b5fd" }} />
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em" }}>Items / sizes</Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary }}>· optional</Typography>

                          {/* Manual product: always show Add item */}
                          {outsideManual.product && (
                            <Button size="small" onClick={addOutsideProductItem}
                              sx={{ ml: "auto", fontSize: "0.72rem", fontWeight: 600, color: "#c4b5fd", backgroundColor: "rgba(139,92,246,.15)", borderRadius: "7px", px: 1, py: 0.3, minWidth: 0, textTransform: "none", "&:hover": { backgroundColor: "rgba(139,92,246,.25)" } }}>
                              <Add sx={{ fontSize: "0.8rem", mr: 0.3 }} />Add item
                            </Button>
                          )}

                          {/* System product: existing canAddMoreSizes logic */}
                          {!outsideManual.product && canAddMoreSizes && (
                            <Button size="small"
                              onClick={() => {
                                const key = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                                setSizeQtyRows((prev) => [...prev, { key, productItemId: null, quantity: 1 }]);
                              }}
                              sx={{ ml: "auto", fontSize: "0.72rem", fontWeight: 600, color: "#c4b5fd", backgroundColor: "rgba(139,92,246,.15)", borderRadius: "7px", px: 1, py: 0.3, minWidth: 0, textTransform: "none", "&:hover": { backgroundColor: "rgba(139,92,246,.25)" } }}>
                              <Add sx={{ fontSize: "0.8rem", mr: 0.3 }} />Add size
                            </Button>
                          )}
                        </Box>

                        {/* ── MANUAL PRODUCT: free-text rows (mirrors PastOrder) ── */}
                        {outsideManual.product && (
                          <>
                            {outsideProductItems.length === 0 ? (
                              <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <InfoOutlined sx={{ fontSize: "0.75rem" }} />Leave empty for a simple product, or add sizes/variants.
                              </Typography>
                            ) : (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                                {/* header */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 0.3, mb: 0.2 }}>
                                  <Box sx={{ width: 18, flexShrink: 0 }} />
                                  <Typography sx={{ flex: 1, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em" }}>Name</Typography>
                                  <Typography sx={{ width: 90, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>Unit Price</Typography>
                                  <Typography sx={{ width: 80, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0, textAlign: "center" }}>Qty</Typography>
                                  <Typography sx={{ width: 72, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0, textAlign: "right" }}>Total</Typography>
                                  <Box sx={{ width: 28, flexShrink: 0 }} />
                                </Box>

                                {outsideProductItems.map((it, idx) => (
                                  <Box key={it.id} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                    <Typography sx={{ width: 18, fontSize: "0.72rem", color: mTokens.textSecondary, textAlign: "center", flexShrink: 0 }}>{idx + 1}</Typography>
                                    {/* Name */}
                                    <TextField
                                      size="small"
                                      value={it.label}
                                      onChange={(e) => setOutsideProductItem(it.id, { label: e.target.value })}
                                      placeholder="e.g. 2×2 cm, Large"
                                      sx={{
                                        flex: 1, minWidth: 0,
                                        ...inputFieldSx(),
                                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                                        "& .MuiInputBase-input": { py: "6px" },
                                      }}
                                    />
                                    {/* Unit Price */}
                                    <TextField
                                      size="small"
                                      value={it.price}
                                      onChange={(e) => {
                                        setOutsideProductItem(it.id, { price: sanitizeCurrencyInput(e.target.value) });
                                      }}
                                      placeholder="0.00"
                                      InputProps={{
                                        startAdornment: <Typography sx={{ mr: 0.3, color: mTokens.textSecondary, fontSize: "0.82rem", flexShrink: 0 }}>$</Typography>,
                                      }}
                                      sx={{
                                        width: 90, flexShrink: 0,
                                        ...inputFieldSx(),
                                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                                        "& .MuiInputBase-input": { py: "6px" },
                                      }}
                                    />
                                    {/* Qty stepper */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, p: 0.3, border: `1px solid ${mTokens.inputBorder}`, borderRadius: "8px", flexShrink: 0, width: 100, justifyContent: "space-between" }}>
                                      <IconButton size="small"
                                        onClick={() => setOutsideProductItem(it.id, { qty: Math.max(1, it.qty - 1) })}
                                        sx={{ p: 0.3, color: mTokens.textSecondary }}>
                                        <Remove sx={{ fontSize: "0.8rem" }} />
                                      </IconButton>
                                      <Typography sx={{ width: 22, textAlign: "center", fontSize: "0.82rem", fontWeight: 700, color: mTokens.textPrimary }}>{it.qty}</Typography>
                                      <IconButton size="small"
                                        onClick={() => setOutsideProductItem(it.id, { qty: it.qty + 1 })}
                                        sx={{ p: 0.3, color: "#c4b5fd", backgroundColor: "rgba(139,92,246,.15)" }}>
                                        <Add sx={{ fontSize: "0.8rem" }} />
                                      </IconButton>
                                    </Box>
                                    {/* Line total */}
                                    <Typography sx={{ width: 72, flexShrink: 0, textAlign: "right", fontSize: "0.84rem", fontWeight: 700, color: parseCurrencyInput(it.price) > 0 ? mTokens.accent : mTokens.textSecondary }}>
                                      {parseCurrencyInput(it.price) > 0 ? `$${(it.qty * parseCurrencyInput(it.price)).toFixed(2)}` : "—"}
                                    </Typography>
                                    {/* Delete */}
                                    <IconButton size="small"
                                      onClick={() => delOutsideProductItem(it.id)}
                                      sx={{ color: mTokens.textSecondary, flexShrink: 0 }}>
                                      <Close sx={{ fontSize: "0.85rem" }} />
                                    </IconButton>
                                  </Box>
                                ))}

                                {/* Items total */}
                                {outsideProductItems.some(it => parseCurrencyInput(it.price) > 0) && (
                                  <Box display="flex" alignItems="center" justifyContent="space-between" pt={1} mt={0.2} sx={{ borderTop: `1px solid ${mTokens.inputBorder}` }}>
                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em" }}>Items total</Typography>
                                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, color: mTokens.accent }}>
                                      ${outsideProductItems.reduce((s, it) => s + it.qty * parseCurrencyInput(it.price), 0).toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </>
                        )}

                        {/* ── SYSTEM PRODUCT: existing dropdown rows ── */}
                        {!outsideManual.product && (
                          <>
                            {sizeQtyRows.length === 0 ? (
                              <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <InfoOutlined sx={{ fontSize: "0.75rem" }} />Leave empty for a simple product, or add sizes/variants.
                              </Typography>
                            ) : (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                                {/* header row */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 0.3, mb: 0.2 }}>
                                  <Box sx={{ width: 18, flexShrink: 0 }} />
                                  <Typography sx={{ flex: 1, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em" }}>Name</Typography>
                                  <Typography sx={{ width: 90, ml: 1, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>Unit Price</Typography>
                                  <Typography sx={{ width: 90, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0, textAlign: "center" }}>Qty</Typography>
                                  <Typography sx={{ width: 72, fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0, textAlign: "right" }}>Total</Typography>
                                  <Box sx={{ width: 28, flexShrink: 0 }} />
                                </Box>
                                {sizeQtyRows.map((row, idx) => {
                                  const allSizeOptions = listData.map((d) => ({ value: d.id, label: d.graftsNeeded }));
                                  const usedIds = new Set(
                                    sizeQtyRows
                                      .filter((r) => r.productItemId != null && r.key !== row.key)
                                      .map((r) => Number(r.productItemId))
                                  );
                                  const availableOptions = allSizeOptions.filter(
                                    (opt) => !usedIds.has(Number(opt.value)) || Number(opt.value) === Number(row.productItemId)
                                  );
                                  const unitPrice = getRowUnitPrice(row.productItemId);
                                  const lineTotal = unitPrice != null && row.quantity > 0 ? unitPrice * row.quantity : null;
                                  return (
                                    <Box key={row.key} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                      <Typography sx={{ width: 18, fontSize: "0.72rem", color: mTokens.textSecondary, textAlign: "center", flexShrink: 0 }}>{idx + 1}</Typography>
                                      <Box sx={{ flex: 1, minWidth: 0, ...dropdownWrapSx() }}>
                                        <CustomDropdownField
                                          control={control as any}
                                          name={`sizeQty.${row.key}` as any}
                                          options={availableOptions}
                                          label=""
                                          placeholderText="Select size…"
                                          size="small"
                                          sortOptions={false}
                                          rules={{ required: "Size is required" }}
                                          onChangeSelect={(_, value) => {
                                            setSizeQtyRows((prev) =>
                                              prev.map((p) => {
                                                if (p.key !== row.key) return p;
                                                const nextId = value !== "" && value !== null && value !== undefined ? Number(value) : null;
                                                if (p.productItemId && p.productItemId !== nextId) setRowQuantity(p.productItemId, 0);
                                                if (nextId != null) setRowQuantity(nextId, p.quantity || 1);
                                                return { ...p, productItemId: nextId, quantity: p.quantity || 1 };
                                              })
                                            );
                                          }}
                                        />
                                      </Box>
                                      <TextField
                                        size="small"
                                        value={unitPrice != null ? unitPrice.toFixed(2) : ""}
                                        placeholder="—"
                                        InputProps={{
                                          readOnly: true,
                                          startAdornment: <Typography sx={{ mr: 0.3, color: mTokens.textSecondary, fontSize: "0.82rem", flexShrink: 0 }}>$</Typography>,
                                        }}
                                        sx={{
                                          width: 90, flexShrink: 0, ml: 1,
                                          ...inputFieldSx(),
                                          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                                          "& .MuiInputBase-input": { py: "6px" },
                                        }}
                                      />
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, p: 0.4, border: `1px solid ${mTokens.inputBorder}`, borderRadius: "8px", flexShrink: 0, width: 100, justifyContent: "space-between" }}>
                                        <IconButton size="small"
                                          onClick={() => setSizeQtyRows((prev) => prev.map((p) => {
                                            if (p.key !== row.key) return p;
                                            const nextQty = Math.max(0, (p.quantity || 0) - 1);
                                            if (p.productItemId != null) setRowQuantity(p.productItemId, nextQty);
                                            return { ...p, quantity: nextQty };
                                          }))}
                                          sx={{ p: 0.3, color: mTokens.textSecondary }}>
                                          <Remove sx={{ fontSize: "0.8rem" }} />
                                        </IconButton>
                                        <Typography sx={{ width: 22, textAlign: "center", fontSize: "0.82rem", fontWeight: 700, color: mTokens.textPrimary }}>{row.quantity ?? 0}</Typography>
                                        <IconButton size="small"
                                          onClick={() => setSizeQtyRows((prev) => prev.map((p) => {
                                            if (p.key !== row.key) return p;
                                            const nextQty = (p.quantity || 0) + 1;
                                            if (p.productItemId != null) setRowQuantity(p.productItemId, nextQty);
                                            return { ...p, quantity: nextQty };
                                          }))}
                                          sx={{ p: 0.3, color: "#c4b5fd", backgroundColor: "rgba(139,92,246,.15)" }}>
                                          <Add sx={{ fontSize: "0.8rem" }} />
                                        </IconButton>
                                      </Box>
                                      <Typography sx={{ width: 72, flexShrink: 0, textAlign: "right", fontSize: "0.84rem", fontWeight: 700, color: lineTotal != null && lineTotal > 0 ? mTokens.accent : mTokens.textSecondary }}>
                                        {lineTotal != null && lineTotal > 0 ? `$${lineTotal.toFixed(2)}` : "—"}
                                      </Typography>
                                      <IconButton size="small"
                                        onClick={() => {
                                          if (row.productItemId != null) setRowQuantity(row.productItemId, 0);
                                          unregister(`sizeQty.${row.key}` as never);
                                          setSizeQtyRows((prev) => prev.filter((p) => p.key !== row.key));
                                        }}
                                        sx={{ color: mTokens.textSecondary, flexShrink: 0 }}>
                                        <Close sx={{ fontSize: "0.85rem" }} />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                                {/* Items total */}
                                {sizeQtyRows.some(r => { const p = getRowUnitPrice(r.productItemId); return p != null && p > 0 && r.quantity > 0; }) && (
                                  <Box display="flex" alignItems="center" justifyContent="space-between" pt={1} mt={0.2} sx={{ borderTop: `1px solid ${mTokens.inputBorder}` }}>
                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: ".04em" }}>Items total</Typography>
                                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, color: mTokens.accent }}>
                                      ${sizeQtyRows.reduce((s, r) => s + r.quantity * (getRowUnitPrice(r.productItemId) || 0), 0).toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                    </Grid>
                  </Grid>
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader
                icon={<InfoOutlined />}
                title="Order Details"
                action={<Typography sx={{ fontSize: "0.75rem", color: mTokens.textSecondary }}>Step 2 of 2</Typography>}
              />
              <Box sx={{ px: 2.8, pt: 1.8, pb: 2.2 }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <FieldLabel label="Date of Service" required={requireDateOfService} />
                    <Box sx={datepickerWrapSx(!!errors.dateOfService)}>
                      <CustomDatePicker
                        key={requireDateOfService ? "date-of-service-required" : "date-of-service-optional"}
                        control={control}
                        onChange={handleDateChangeService}
                        controllerName="dateOfService"
                        label=""
                        size="small"
                        rules={dateOfServiceRules}
                      />
                    </Box>
                  </Grid>
                  {isOutsideRecordOrder ? (
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Shipping Address" required={requireShippingAddress} />
                      <Box sx={dropdownWrapSx()}>
                        <CustomDropdownField
                          key={
                            canEnterOutsideManual && outsideManual.shippingAddress
                              ? "shipping-address-manual"
                              : requireShippingAddress
                                ? "shipping-address-platform-required"
                                : "shipping-address-platform-optional"
                          }
                          name={canEnterOutsideManual && outsideManual.shippingAddress ? "shippingAddressManual" : "facilityAddress"}
                          control={control}
                          options={canEnterOutsideManual && outsideManual.shippingAddress ? [] : locationOptions}
                          onChangeSelect={canEnterOutsideManual && outsideManual.shippingAddress ? undefined : handleLocationChange}
                          label=""
                          size="small"
                          comboStyle={!(canEnterOutsideManual && outsideManual.shippingAddress)}
                          withoutSysData={canEnterOutsideManual && outsideManual.shippingAddress}
                          placeholderText={canEnterOutsideManual && outsideManual.shippingAddress ? "Enter shipping address…" : "Select shipping address…"}
                          addManuallyLabel={canEnterOutsideManual ? "Enter manually" : undefined}
                          onAddManually={canEnterOutsideManual ? (val) => goOutsideManual("shippingAddress", val) : undefined}
                          onUseSystem={canEnterOutsideManual && outsideManual.shippingAddress ? () => goOutsideSystem("shippingAddress") : undefined}
                          enableAddressSuggestions
                          fetchFunction={fetchStreetSuggestions}
                          transformResponse={transformAddressResponse}
                          rules={requireShippingAddress ? { required: "Shipping Address is required." } : {}}
                          disabled={(location.state?.orderType === ORDERTYPE.ORDERED_FROM_PATIENT_LIST || location.state?.orderType === ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR || location.state?.orderType === ORDERTYPE.PAST_ORDER) ? false : order?.orderType !== ORDERTYPE.DIRECT_ORDER && isDisableProducts}
                        />
                      </Box>
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Shipping Address" required />
                      <Box sx={dropdownWrapSx()}>
                        <CustomDropdownField
                          name="facilityAddress"
                          control={control}
                          onChangeSelect={handleLocationChange}
                          options={locationOptions}
                          label=""
                          size="small"
                          placeholderText="Select shipping address…"
                          rules={{ required: "Shipping Address is required." }}
                          disabled={(location.state?.orderType === ORDERTYPE.ORDERED_FROM_PATIENT_LIST || location.state?.orderType === ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR || location.state?.orderType === ORDERTYPE.PAST_ORDER) ? false : order?.orderType !== ORDERTYPE.DIRECT_ORDER && isDisableProducts}
                        />
                      </Box>
                    </Grid>
                  )}
                  {isOutsideRecordOrder ? (
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Requested Shipping Method" />
                      <Box sx={dropdownWrapSx()}>
                        <CustomDropdownField
                          key={(canEnterOutsideManual && (outsideManual.shippingMethod || outsideManual.product)) ? "shipping-method-manual" : "shipping-method-platform"}
                          name={(canEnterOutsideManual && (outsideManual.shippingMethod || outsideManual.product)) ? "shippingMethodManual" : "requestedShippingMethod"}
                          control={control}
                          options={shippingMethodOptions}
                          size="small"
                          comboStyle={!(canEnterOutsideManual && (outsideManual.shippingMethod || outsideManual.product))}
                          withoutSysData={canEnterOutsideManual && (outsideManual.shippingMethod || outsideManual.product)}
                          placeholderText={(canEnterOutsideManual && (outsideManual.shippingMethod || outsideManual.product)) ? "Enter shipping method…" : "Select shipping method…"}
                          addManuallyLabel={canEnterOutsideManual ? "Enter manually" : undefined}
                          onAddManually={canEnterOutsideManual && !outsideManual.product ? (val) => goOutsideManual("shippingMethod", val) : undefined}
                          onUseSystem={canEnterOutsideManual && outsideManual.shippingMethod && !outsideManual.product ? () => goOutsideSystem("shippingMethod") : undefined}
                          label=""
                        />
                      </Box>
                    </Grid>) : (
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Requested Shipping Method" required={isShippingRequiredForPayNow} />
                      <Box sx={dropdownWrapSx()}>
                        <CustomDropdownField
                          key={`requested-shipping-${sidebarPaymentMode ?? "unset"}`}
                          control={control}
                          name="requestedShippingMethod"
                          options={shippingMethodOptions}
                          label=""
                          size="small"
                          placeholderText="Select shipping method…"
                          sortOptions={false}
                          rules={shippingMethodRules}
                        />
                      </Box>
                    </Grid>
                  )}
                  {isOutsideRecordOrder && outsideManual.product && (
                    <Grid item xs={12} md={3}>
                      <FieldLabel label="Order Price" required />
                      <Box sx={inputFieldSx()}>
                        <CustomTextField
                          controllerName="outsideOrderPrice"
                          control={control}
                          label=""
                          size="small"
                          isNumberOnly
                          placeholder="0.00"
                          rules={{ required: "Please enter the order price." }}
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 0.5, color: mTokens.textSecondary, fontSize: "0.88rem" }}>$</Typography>
                            ),
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                  {isOutsideRecordOrder && (outsideManual.shippingMethod || outsideManual.product) && (
                    <Grid item xs={12} md={3}>
                      <FieldLabel label="Shipping Price" />
                      <Box sx={inputFieldSx()}>
                        <CustomTextField controllerName="outsideShipPrice" control={control} label="" size="small"
                          isNumberOnly placeholder="0.00"
                          InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: mTokens.textSecondary, fontSize: "0.88rem" }}>$</Typography> }} />
                      </Box>
                    </Grid>
                  )}
                  {/* Notes — show for outside orders because they are saved through the outside-order endpoint. */}
                  {isOutsideRecordOrder && shouldSaveOutsideOrder && (
                    <Grid item xs={12}>
                      <FieldLabel label="Notes" />
                      <Box sx={inputFieldSx()}>
                        <CustomTextField controllerName="outsideNotes" control={control} label="" size="small"
                          multiline rows={3} maxLength={500}
                          placeholder="PO number, reason for backfill, original invoice ref…" />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>

          </Box>

          <Box sx={{ position: "sticky", top: 12, display: "flex", flexDirection: "column", gap: 1.5 }}>

            <Box sx={cardSx}>
              <SectionHeader icon={<PersonOutlined />} title="Order Information" />
              <Box sx={{ px: 2.8, py: 1 }}>
                {/* Patient */}
                <Box display="flex" alignItems="center" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>Patient</Typography>
                  <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, flex: 1 }}>
                    {displayPatientNameResolved || "—"}
                  </Typography>
                </Box>
                {/* Product */}
                {summaryProductName ? (
                  <Box display="flex" alignItems="center" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: 0.8, borderRadius: "10px", backgroundColor: mTokens.cardBg, border: `1px solid ${mTokens.border}`, flex: 1, minWidth: 0 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: "10px", backgroundColor: `${mTokens.accent}18`, border: `1px solid ${mTokens.accent}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <ViewInArOutlined sx={{ fontSize: "1.1rem", color: mTokens.accent }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: mTokens.textPrimary, lineHeight: 1.3 }}>{summaryProductName}</Typography>
                        {summaryManufacturerName && <Typography sx={{ fontSize: "0.75rem", color: mTokens.textSecondary, mt: 0.1 }}>by {summaryManufacturerName}</Typography>}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                    <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>Product</Typography>
                    <Typography sx={{ fontSize: "0.88rem", color: mTokens.textSecondary, flex: 1 }}>—</Typography>
                  </Box>
                )}
                {/* Practice */}
                <Box display="flex" alignItems="center" py={0.8} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>Practice</Typography>
                  <Typography sx={{ fontSize: "0.88rem", color: displayPracticeNameResolved ? mTokens.textPrimary : mTokens.textSecondary, fontWeight: displayPracticeNameResolved ? 600 : 400, flex: 1 }}>
                    {displayPracticeNameResolved || "—"}
                  </Typography>
                </Box>
                {/* Date of Service */}
                <Box display="flex" alignItems="center" py={0.8}>
                  <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>Date of Service</Typography>
                  <Typography sx={{ fontSize: "0.88rem", color: dateOfService ? mTokens.textPrimary : mTokens.textSecondary, fontWeight: dateOfService ? 600 : 400, flex: 1 }}>{dateOfService || "—"}</Typography>
                </Box>
              </Box>
            </Box>

            <OrderCalculationCard
              layout="sidebar"
              productName={summaryProductName}
              manufacturerName={summaryManufacturerName}
              providerSubtitle={providerSummarySubtitle}

              // ↓ Switch between system and manual data
              productItems={outsideManual.product ? outsideSummaryItems : summaryProductItems}
              quantities={outsideManual.product ? outsideOrderQuantities : orderQuantities}
              hasSku={outsideManual.product ? outsideProductItems.length > 0 : resolvedOrderPricing.hasSku}
              skuAllowablePrices={outsideManual.product ? outsideSkuPrices : cardSkuAllowablePrices}
              extendedPrice={outsideManual.product ? null : resolvedOrderPricing.extendedPrice}
              upfrontPrice={outsideManual.product ? null : (hasUpfrontDiscount ? resolvedOrderPricing.upfrontPrice : null)}

              shippingCost={resolvedShippingCost}
              shippingLabel={resolvedShippingLabel}
              // CS-1766 - free shipping: show the original price struck through as waived
              shippingWaivedAmount={shippingWaivedAmount}
              discountPercent={outsideManual.product || !hasUpfrontDiscount ? 0 : resolvedOrderPricing.upfrontDiscount}
              savedOrderItemPrices={
                isOutsideOrderEdit && !outsideManual.product
                  ? outsideEditPlatformOrderItemPrices
                  : null
              }
              showUpfrontOrderUi={shouldSaveOutsideOrder ? false : showUpfrontOrderUi}
              allowEmptyItems={outsideManual.product}
              manualOrderPrice={outsideManual.product ? parseFloat(watch("outsideOrderPrice" as any) || "") || null : null}
              onTotalCalculated={setUpfrontCardTotal}
              onPaymentModeChange={setSidebarPaymentMode}
              onPayLater={shouldSaveOutsideOrder ? handleSaveOutsideOrder : handlePayLaterClick}
              onPayNow={shouldSaveOutsideOrder ? handleSaveOutsideOrder : handleSubmit(() => onSubmit(true))}
              submitActionLabel={isOutsideOrderEdit ? "Update" : undefined}
              payLaterActionLabel={shouldSaveOutsideOrder ? (isOutsideOrderEdit ? "Update" : "Save Outside Order") : "Place order (pay later)"}
              onCancel={handleNavigation}
            />
          </Box>
        </Box>
      </form>

      {/* CS-1845 - Stripe Payment Popup for Upfront Orders */}
      {showStripePaymentTypePopup && (
        <StripePaymentTypePopup
          open={showStripePaymentTypePopup}
          onClose={() => setShowStripePaymentTypePopup(false)}
          invoiceData={stripeInvoiceData}
          isUpfrontPayment={true}
          createCheckoutSession={handleCheckout}
          isProcessingPayment={isProcessingStripePayment}
          checkoutError={checkoutError}
          onErrorCancel={() => {
            setShowStripePaymentTypePopup(false);
            navigate("/order_list");
          }}
        />
      )}
    </Box>
  );
};

export default Order;
